import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, User, FileText, Handshake, FolderOpen, Wallet, Settings, LogOut, Link2, CheckCircle, XCircle, Clock, Shield, ChevronRight, Save, ChevronDown, ChevronUp, MessageSquare, Eye, ArrowRight, ArrowLeft, Send, Globe, MapPin, Calendar, Briefcase, DollarSign, TrendingUp, AlertCircle, Ban, Lock, Upload, Plus, Trash2, Image, Video, Mic, File as FileIcon, ExternalLink, Zap, Bell, HelpCircle, Trash, KeyRound, Download, GitCompare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import API from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import CreatorProposalDetail from './CreatorProposalDetail';
import { useOnboarding } from '@/hooks/useOnboarding';
import { WelcomeModal } from '@/components/onboarding/WelcomeModal';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import { ProductTour } from '@/components/onboarding/ProductTour';
import { TOUR_STEPS } from '@/components/onboarding/onboardingConfig';
import { MediationBox } from '@/components/MediationBox';
import ProposalDiffViewer from '@/components/ProposalDiffViewer';
import SupportDialog from '@/components/SupportDialog';

const CATEGORIES = ['Fashion', 'Technology', 'Gaming', 'Fitness', 'Wellness', 'Travel', 'Food', 'Beauty', 'Lifestyle', 'Entertainment', 'Sports', 'Finance', 'Education'];
const PLATFORMS = ['Instagram', 'TikTok', 'YouTube', 'Twitter/X', 'Facebook', 'LinkedIn', 'Snapchat', 'Pinterest'];
const TERRITORIES = ['United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'Japan', 'Brazil', 'Global'];
const COUNTRY_CODE_NAMES = { US: 'United States', GB: 'United Kingdom', UK: 'United Kingdom', CA: 'Canada', AU: 'Australia', DE: 'Germany', FR: 'France', JP: 'Japan', BR: 'Brazil' };
const CLAIMS_RESTRICTIONS = ['Medical claims', 'Financial guarantees', 'Political advocacy', 'Religious statements', 'Competitor comparisons'];

function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    completed: 'bg-[#F6F8FC] text-[#475467] border-[#D0D5DD]',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    pending_review: 'bg-amber-50 text-amber-700 border-amber-200',
    submitted: 'bg-blue-50 text-blue-700 border-blue-200',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    proposed: 'bg-blue-50 text-blue-700 border-blue-200',
    draft: 'bg-[#F6F8FC] text-[#475467] border-[#D0D5DD]',
    countered: 'bg-purple-50 text-purple-700 border-purple-200',
    negotiating: 'bg-purple-50 text-purple-700 border-purple-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    denied: 'bg-red-50 text-red-700 border-red-200',
    changes_requested: 'bg-orange-50 text-orange-700 border-orange-200',
    funded: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    paid_out: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    funds_secured: 'bg-blue-50 text-blue-700 border-blue-200',
    awaiting_creator_approval: 'bg-amber-50 text-amber-700 border-amber-200',
    payout_scheduled: 'bg-teal-50 text-teal-700 border-teal-200',
    verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    unverified: 'bg-amber-50 text-amber-700 border-amber-200',
    processing: 'bg-blue-50 text-blue-700 border-blue-200',
    requires_input: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  const label = status?.replace(/_/g, ' ');
  return <Badge variant="outline" className={`text-xs font-medium capitalize ${map[status] || 'bg-[#F6F8FC] text-[#475467]'}`}>{label}</Badge>;
}

function SectionCard({ children, className = '', ...rest }) {
  return <div className={`bg-white rounded-2xl border border-[#E5E7EB] p-6 ${className}`} {...rest}>{children}</div>;
}

export default function CreatorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [profile, setProfile] = useState(null);
  const [dashStats, setDashStats] = useState({});
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [tourRun, setTourRun] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const p = await API.get('/creator/profile');
      setProfile(p.data.profile);
    } catch { toast.error('Failed to load profile'); }
  }, []);

  const fetchDashStats = useCallback(async () => {
    try {
      const res = await API.get('/creator/dashboard-stats');
      setDashStats(res.data);
    } catch {}
  }, []);

  const onboarding = useOnboarding({ onExampleSeeded: fetchDashStats });

  // Fetch profile+stats AND refresh checklist whenever a mutation could have
  // ticked a step (avatar upload, niche, bio, complete profile, etc.).
  const refreshAll = useCallback(() => {
    fetchProfile();
    fetchDashStats();
    onboarding.refetch();
  }, [fetchProfile, fetchDashStats, onboarding]);

  useEffect(() => { fetchProfile(); fetchDashStats(); }, [fetchProfile, fetchDashStats]);

  // Whenever the user returns to the Dashboard tab, re-check checklist progress.
  useEffect(() => {
    if (tab === 'dashboard') onboarding.refetch();
  }, [tab, onboarding]);

  useEffect(() => {
    if (onboarding.data && !onboarding.data.state?.welcome_dismissed) setWelcomeOpen(true);
  }, [onboarding.data]);

  const closeWelcome = () => { setWelcomeOpen(false); onboarding.patch({ welcome_dismissed: true }); };
  const startTour = () => { setTourRun(true); };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'proposals', label: 'Proposals', icon: FileText, badge: dashStats.pending_proposals },
    { id: 'engagements', label: 'Engagements', icon: Handshake, badge: dashStats.active_engagements },
    { id: 'likeness', label: 'Likeness Pack', icon: FolderOpen },
    { id: 'payouts', label: 'Payouts', icon: Wallet },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const copyProfileLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/creator/${profile?.public_id}`);
    toast.success('Profile link copied!');
  };

  return (
    <div className="flex min-h-screen bg-[#F8F9FB]" data-testid="creator-dashboard">
      <aside className="w-64 bg-white border-r border-[#E5E7EB] flex flex-col">
        <div className="p-6 border-b border-[#E5E7EB]">
          <button onClick={() => navigate('/')} className="font-heading text-xl font-semibold text-[#101828]">Mylikeness</button>
          <p className="text-[#2563EB] text-xs font-medium mt-0.5">Creator</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => (
            <button key={item.id} data-testid={`nav-${item.id}`} onClick={() => setTab(item.id)}
              className={`sidebar-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${tab === item.id ? 'bg-[#2563EB] text-white' : 'text-[#475467] hover:bg-[#F6F8FC]'}`}>
              <item.icon className="w-4 h-4" />
              <span className="flex-1 text-left">{item.label}</span>
              {item.badge > 0 && <span className={`text-xs px-2 py-0.5 rounded-full ${tab === item.id ? 'bg-white/20 text-white' : 'bg-[#2563EB] text-white'}`}>{item.badge}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E5E7EB]">
          <div className="flex items-center gap-3 mb-3 p-2 rounded-xl bg-[#F6F8FC]">
            {(() => {
              const src = profile?.avatar_url
                ? (profile.avatar_url.startsWith('http') ? profile.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${profile.avatar_url}`)
                : null;
              return src ? (
                <img src={src} alt={user?.display_name} data-testid="sidebar-avatar"
                  className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0" />
              ) : (
                <div data-testid="sidebar-avatar-fallback"
                  className="w-10 h-10 rounded-full bg-[#EEF2FA] text-[#2563EB] font-semibold flex items-center justify-center border-2 border-white shadow-sm flex-shrink-0">
                  {user?.display_name?.[0] || '?'}
                </div>
              );
            })()}
            <div className="flex-1 min-w-0">
              <p className="text-[#101828] text-sm font-medium truncate">{user?.display_name}</p>
              <p className="text-[#475467] text-xs font-mono truncate">{profile?.public_id}</p>
            </div>
          </div>
          <Button data-testid="creator-logout-btn" variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} className="w-full text-[#475467] hover:text-[#101828] hover:bg-[#F6F8FC] text-xs">
            <LogOut className="w-3 h-3 mr-2" />Sign Out
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-auto">
        {tab === 'dashboard' && <DashboardView profile={profile} stats={dashStats} onCopyLink={copyProfileLink} setTab={setTab} onboarding={onboarding} onStartTour={startTour} />}
        {tab === 'profile' && <ProfileView profile={profile} onRefresh={refreshAll} />}
        {tab === 'proposals' && <ProposalsView />}
        {tab === 'engagements' && <EngagementsView />}
        {tab === 'likeness' && <LikenessPackView />}
        {tab === 'payouts' && <PayoutsView />}
        {tab === 'settings' && <SettingsView onRefresh={refreshAll} />}
      </main>

      <WelcomeModal
        open={welcomeOpen}
        role="creator"
        name={user?.display_name}
        onClose={closeWelcome}
        onStartTour={() => { closeWelcome(); startTour(); }}
      />
      <ProductTour
        run={tourRun}
        steps={TOUR_STEPS.creator}
        onClose={() => setTourRun(false)}
        onComplete={() => onboarding.patch({ tour_completed: true })}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   DASHBOARD OVERVIEW
   ═══════════════════════════════════════════════════════════ */

function DashboardView({ profile, stats, onCopyLink, setTab, onboarding, onStartTour }) {
  const s = stats || {};
  const ob = onboarding?.data;

  // Auto-dismiss checklist when all steps are done
  useEffect(() => {
    if (!ob || ob.state?.checklist_dismissed) return;
    const p = ob.progress;
    if (p && p.done >= p.total && p.total > 0) {
      const t = setTimeout(() => onboarding.patch({ checklist_dismissed: true }), 3000);
      return () => clearTimeout(t);
    }
  }, [ob, onboarding]);

  const statCards = [
    { label: 'Pending Proposals', value: s.pending_proposals || 0, icon: FileText, color: 'bg-blue-50 text-blue-600', click: () => setTab('proposals') },
    { label: 'Active Engagements', value: s.active_engagements || 0, icon: Handshake, color: 'bg-emerald-50 text-emerald-600', click: () => setTab('engagements') },
    { label: 'Total Earned', value: `$${(s.total_earned || 0).toLocaleString()}`, icon: DollarSign, color: 'bg-[#EEF2FA] text-[#2563EB]', click: () => setTab('payouts') },
    { label: 'Funds Held', value: `$${(s.funds_held || 0).toLocaleString()}`, icon: Lock, color: 'bg-amber-50 text-amber-600', click: () => setTab('payouts') },
  ];

  return (
    <div data-testid="creator-overview">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#101828]">Welcome back, {profile?.display_name?.split(' ')[0]}</h1>
          <div className="flex items-center gap-3 mt-2">
            <StatusBadge status={profile?.verification_status || 'unverified'} />
            <button onClick={onCopyLink} className="text-[#2563EB] text-sm font-medium hover:underline flex items-center gap-1">
              <Link2 className="w-3 h-3" /> Copy Profile Link
            </button>
          </div>
        </div>
        {ob?.state?.checklist_dismissed && (
          <OnboardingChecklist
            checklist={ob.checklist} progress={ob.progress} role={ob.role}
            dismissed onReopen={() => onboarding.patch({ checklist_dismissed: false })}
          />
        )}
      </div>

      {ob && !ob.state?.checklist_dismissed && (
        <div className="mb-8">
          <OnboardingChecklist
            checklist={ob.checklist} progress={ob.progress} role={ob.role}
            onAction={(t) => setTab(t)}
            onStartTour={onStartTour}
            dismissed={false}
            onDismiss={() => onboarding.patch({ checklist_dismissed: true })}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {statCards.map(c => (
          <button key={c.label} onClick={c.click} className="bg-white rounded-2xl border border-[#E5E7EB] p-5 text-left hover:shadow-md transition-shadow">
            <div className={`w-10 h-10 rounded-xl ${c.color} flex items-center justify-center mb-4`}>
              <c.icon className="w-5 h-5" />
            </div>
            <p className="text-3xl font-semibold text-[#101828]">{c.value}</p>
            <p className="text-[#475467] text-sm mt-1">{c.label}</p>
          </button>
        ))}
      </div>

      {(s.pending_proposals > 0 || s.content_to_review > 0) && (
        <div className="space-y-3 mb-8">
          {s.pending_proposals > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 flex items-center gap-4 cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => setTab('proposals')}>
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center"><FileText className="w-5 h-5 text-blue-600" /></div>
              <div className="flex-1">
                <p className="text-blue-800 font-medium">You have {s.pending_proposals} pending proposal{s.pending_proposals > 1 ? 's' : ''}</p>
                <p className="text-blue-600 text-sm">Review and respond to new offers</p>
              </div>
              <ChevronRight className="w-5 h-5 text-blue-400" />
            </div>
          )}
          {s.content_to_review > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-4 cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => setTab('engagements')}>
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><Eye className="w-5 h-5 text-amber-600" /></div>
              <div className="flex-1">
                <p className="text-amber-800 font-medium">{s.content_to_review} content item{s.content_to_review > 1 ? 's' : ''} need your review</p>
                <p className="text-amber-600 text-sm">Approve or request changes on submitted content</p>
              </div>
              <ChevronRight className="w-5 h-5 text-amber-400" />
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button onClick={() => setTab('proposals')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#F6F8FC] hover:bg-[#EEF2FA] transition-colors text-left">
              <FileText className="w-5 h-5 text-[#2563EB]" /><span className="text-sm font-medium text-[#101828]">Review Proposals</span><ChevronRight className="w-4 h-4 text-[#475467] ml-auto" />
            </button>
            <button onClick={() => setTab('engagements')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#F6F8FC] hover:bg-[#EEF2FA] transition-colors text-left">
              <Handshake className="w-5 h-5 text-emerald-600" /><span className="text-sm font-medium text-[#101828]">Active Engagements</span><ChevronRight className="w-4 h-4 text-[#475467] ml-auto" />
            </button>
            <button onClick={() => setTab('likeness')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#F6F8FC] hover:bg-[#EEF2FA] transition-colors text-left">
              <FolderOpen className="w-5 h-5 text-purple-600" /><span className="text-sm font-medium text-[#101828]">Manage Likeness Pack</span><ChevronRight className="w-4 h-4 text-[#475467] ml-auto" />
            </button>
          </div>
        </SectionCard>

        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-4">Payout Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]">
              <span className="text-[#475467] text-sm">Total Earned</span>
              <span className="text-[#101828] font-semibold">${(s.total_earned || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]">
              <span className="text-[#475467] text-sm">Funds Held (pending approval)</span>
              <span className="text-amber-600 font-semibold">${(s.funds_held || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-[#475467] text-sm">Pending Release</span>
              <span className="text-blue-600 font-semibold">${(s.pending_payout || 0).toLocaleString()}</span>
            </div>
          </div>
        </SectionCard>
      </div>

      <DashboardHelpCenter setTab={setTab} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   HELP CENTER (Dashboard) — Tips, Getting Started, FAQ
   ═══════════════════════════════════════════════════════════ */
function DashboardHelpCenter({ setTab }) {
  const [openItem, setOpenItem] = useState(null);
  const [activePanel, setActivePanel] = useState('start');
  const [supportOpen, setSupportOpen] = useState(false);

  const PANELS = [
    { id: 'start', label: 'Getting started', icon: Zap },
    { id: 'tips', label: 'Quick tips', icon: Briefcase },
    { id: 'faq', label: 'FAQ', icon: MessageSquare },
    { id: 'tutorial', label: 'Tutorial', icon: Eye },
  ];

  const GETTING_STARTED = [
    { step: 1, title: 'Complete your profile', body: 'Add a clear bio, niche, and high-quality photos. Agencies use this to decide whether to send proposals.', cta: { label: 'Edit profile', tab: 'profile' } },
    { step: 2, title: 'Build your Likeness Pack', body: 'Upload diverse poses, expressions, and reference images. This is what agencies use to generate AI content featuring you.', cta: { label: 'Open Likeness Pack', tab: 'likeness' } },
    { step: 3, title: 'Set your permissions', body: 'Configure allowed categories, banned categories, and claims restrictions in Settings → Permissions. Agencies see these before sending offers.', cta: { label: 'Configure permissions', tab: 'settings' } },
    { step: 4, title: 'Get verified', body: 'Verify your identity with Stripe (ID + selfie) in Settings → Identity. Verified creators earn 3-4× more on average.', cta: { label: 'Start verification', tab: 'settings' } },
    { step: 5, title: 'Connect your bank', body: 'Set up Stripe Express in Payouts. You can do this now or wait until your first earned payment.', cta: { label: 'Set up payouts', tab: 'payouts' } },
  ];

  const TIPS = [
    { icon: '👤', title: 'Pro-grade photos win', body: 'Profiles with 5+ professional photos receive 4× more proposals.' },
    { icon: '🎯', title: 'Be specific about your niche', body: 'A focused niche ("fitness wellness") gets better deals than "lifestyle".' },
    { icon: '⏱', title: 'Reply within 24 hours', body: 'Top creators respond fast — agencies often have multiple options.' },
    { icon: '💰', title: 'Counter, don\'t refuse', body: 'Most agencies expect negotiation. Try a counter-offer before declining.' },
    { icon: '🔒', title: 'Lock down banned categories', body: 'Update your restrictions in Settings so you never see proposals you\'d reject anyway.' },
    { icon: '📊', title: 'Royalties beat one-offs', body: 'Subscription/royalty deals build passive income — check Payouts → Projected royalty.' },
  ];

  const FAQ = [
    { q: 'How do I receive my first payout?', a: 'Once you approve content in an engagement, the funds become "ready to release". Set up Stripe Express in Payouts (5 mins) and click "Release now" to send the money to your bank.' },
    { q: 'When do funds become released?', a: 'After (1) the agency funds the deal, (2) the agency uploads the AI-generated content, (3) you review and approve it. Funds typically hit your bank 2-7 business days after release via Stripe.' },
    { q: 'What\'s the commission on each deal?', a: 'Mylikeness charges agencies a sliding-scale commission per deal: 10% under $5k, 8% to $10k, 7% to $20k, 5% to $50k, 3% above $50k. The amount you see in your "net payout" is what you receive after commission.' },
    { q: 'Can I refuse a proposal without consequences?', a: 'Absolutely. Refusing has no impact on your future proposals. Some creators refuse 70% of incoming offers — that\'s normal. You can also negotiate by sending a counter-offer.' },
    { q: 'What if the agency creates content I don\'t approve of?', a: 'You always have final approval on content. If submitted content violates your permissions/banned categories, click "Request changes" or "Dispute" — funds remain held until resolution.' },
    { q: 'Do I need to verify my identity?', a: 'Yes, eventually. Verified creators (govt ID) earn significantly more and get priority placement. You can browse proposals without verification but cannot receive payouts.' },
    { q: 'Can I delete my account anytime?', a: 'Yes. Settings → Account → Delete account. Pending payouts will be processed before deletion. Your active deals must be settled or cancelled first.' },
    { q: 'How do royalty deals work?', a: 'Some deals are subscription-based — you earn a recurring royalty (%) on ongoing usage. Visit Payouts → Projected royalty earnings to see active subscriptions and expected income.' },
  ];

  return (
    <div className="mt-8" data-testid="dashboard-help-center">
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="px-6 pt-5 pb-4 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <MessageSquare className="w-3.5 h-3.5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-[#101828]">Help center</h3>
          </div>
          <p className="text-[#475467] text-xs">Tips, walkthroughs, and answers to common questions.</p>
        </div>

        <div className="flex border-b border-[#E5E7EB] bg-[#F9FAFB] px-6">
          {PANELS.map(p => (
            <button key={p.id} onClick={() => setActivePanel(p.id)}
              data-testid={`help-tab-${p.id}`}
              className={`flex items-center gap-2 px-3 py-3 text-xs font-medium border-b-2 transition-colors ${
                activePanel === p.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-[#475467] hover:text-[#101828]'
              }`}>
              <p.icon className="w-3.5 h-3.5" /> {p.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activePanel === 'start' && (
            <div className="space-y-3" data-testid="help-getting-started">
              {GETTING_STARTED.map((item) => (
                <div key={item.step} className="flex items-start gap-4 p-4 rounded-xl bg-[#F9FAFB] border border-[#E5E7EB]">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">
                    {item.step}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-[#101828] text-sm">{item.title}</h4>
                    <p className="text-[#475467] text-xs mt-1">{item.body}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setTab(item.cta.tab)}
                    data-testid={`start-cta-${item.step}`}
                    className="h-8 text-xs flex-shrink-0">
                    {item.cta.label} <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {activePanel === 'tips' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="help-tips">
              {TIPS.map((tip, i) => (
                <div key={i} className="p-4 rounded-xl bg-[#F9FAFB] border border-[#E5E7EB]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xl">{tip.icon}</span>
                    <h4 className="font-semibold text-[#101828] text-sm">{tip.title}</h4>
                  </div>
                  <p className="text-[#475467] text-xs leading-relaxed">{tip.body}</p>
                </div>
              ))}
            </div>
          )}

          {activePanel === 'faq' && (
            <div className="space-y-2" data-testid="help-faq">
              {FAQ.map((item, i) => (
                <div key={i} className="border border-[#E5E7EB] rounded-xl overflow-hidden">
                  <button onClick={() => setOpenItem(openItem === i ? null : i)}
                    data-testid={`faq-toggle-${i}`}
                    className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-[#F9FAFB] transition-colors">
                    <span className="text-[#101828] text-sm font-medium">{item.q}</span>
                    {openItem === i ? <ChevronUp className="w-4 h-4 text-[#475467] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#475467] flex-shrink-0" />}
                  </button>
                  {openItem === i && (
                    <div className="px-4 pb-4 pt-1 text-[#475467] text-xs leading-relaxed bg-[#F9FAFB] border-t border-[#E5E7EB]">
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activePanel === 'tutorial' && (
            <div data-testid="help-tutorial" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { title: 'Setting up your profile', duration: '3 min', tab: 'profile' },
                  { title: 'Building your Likeness Pack', duration: '5 min', tab: 'likeness' },
                  { title: 'Reviewing & responding to proposals', duration: '4 min', tab: 'proposals' },
                  { title: 'Managing an active engagement', duration: '6 min', tab: 'engagements' },
                  { title: 'Releasing funds to your bank', duration: '4 min', tab: 'payouts' },
                  { title: 'Customising your permissions', duration: '5 min', tab: 'settings' },
                ].map((t, i) => (
                  <button key={i} onClick={() => setTab(t.tab)}
                    data-testid={`tutorial-${i}`}
                    className="flex items-start gap-3 p-4 rounded-xl border border-[#E5E7EB] hover:border-blue-300 hover:bg-blue-50/30 text-left transition-all">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                      <Eye className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-[#101828] font-semibold text-sm">{t.title}</h4>
                      <p className="text-[#475467] text-xs mt-0.5">~{t.duration} · interactive walk-through</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#475467] flex-shrink-0 mt-1" />
                  </button>
                ))}
              </div>
              <div className="text-center pt-2">
                <p className="text-[#98A2B3] text-[11px]">Interactive tutorial mode coming soon. For now, each card jumps you to the relevant section.</p>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[#475467] text-xs">Need more help?</p>
          <button type="button" onClick={() => setSupportOpen(true)} data-testid="help-contact-support"
            className="text-blue-600 text-xs font-medium hover:underline inline-flex items-center gap-1">
            Contact support <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
      <SupportDialog open={supportOpen} onOpenChange={setSupportOpen} defaultCategory="general" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PROFILE VIEW
   ═══════════════════════════════════════════════════════════ */

function ProfileView({ profile, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [form, setForm] = useState({
    display_name: '', headline: '', bio: '', location: '', pronouns: '',
    languages: [], niches: [],
    instagram: '', twitter: '', youtube: '', tiktok: '', website: '',
    avatar_url: '', cover_url: '',
  });

  const NICHE_OPTIONS = [
    'Fashion','Beauty','Fitness','Wellness','Lifestyle','Travel','Food','Gaming','Tech',
    'Music','Comedy','Education','Parenting','Sports','Business','Auto','Photography','Art','Other',
  ];
  const LANG_OPTIONS = ['English','Spanish','French','German','Mandarin','Japanese','Korean','Arabic','Portuguese','Italian','Hindi','Other'];

  useEffect(() => {
    if (profile) setForm({
      display_name: profile.display_name || '',
      headline: profile.headline || '',
      bio: profile.bio || '',
      location: profile.location || '',
      pronouns: profile.pronouns || '',
      languages: Array.isArray(profile.languages) ? profile.languages : [],
      niches: Array.isArray(profile.niches) ? profile.niches : [],
      instagram: profile.social_links?.instagram || '',
      twitter: profile.social_links?.twitter || '',
      youtube: profile.social_links?.youtube || '',
      tiktok: profile.social_links?.tiktok || '',
      website: profile.social_links?.website || '',
      avatar_url: profile.avatar_url || '',
      cover_url: profile.cover_url || '',
    });
  }, [profile]);

  const uploadImage = async (file, target) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Pick an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    const setter = target === 'avatar' ? setUploadingAvatar : setUploadingCover;
    const key = target === 'avatar' ? 'avatar_url' : 'cover_url';

    // Optimistic preview — show the image immediately while upload happens
    const objectUrl = URL.createObjectURL(file);
    const previousValue = form[key];
    setForm(f => ({ ...f, [key]: objectUrl }));

    setter(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await API.post('/uploads/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, [key]: res.data.url }));
      URL.revokeObjectURL(objectUrl);
      // Auto-save (so the avatar persists even if the user navigates away before clicking Save).
      try {
        await API.patch('/creator/profile', { [key]: res.data.url });
        onRefresh?.();
      } catch { /* will be picked up by the explicit Save button */ }
      toast.success(`${target === 'avatar' ? 'Avatar' : 'Cover'} updated`);
    } catch (e) {
      setForm(f => ({ ...f, [key]: previousValue }));
      URL.revokeObjectURL(objectUrl);
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    finally { setter(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await API.patch('/creator/profile', {
        display_name: form.display_name, headline: form.headline, bio: form.bio,
        location: form.location, pronouns: form.pronouns,
        languages: form.languages, niches: form.niches,
        avatar_url: form.avatar_url, cover_url: form.cover_url,
        social_links: {
          instagram: form.instagram, twitter: form.twitter,
          youtube: form.youtube, tiktok: form.tiktok, website: form.website,
        },
      });
      toast.success('Profile updated');
      setEditing(false);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const toggleArray = (key, val) => setForm(f => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
  }));

  const profileUrl = `${window.location.origin}/creator/${profile?.public_id || ''}`;
  const memberSince = profile?.created_at ? new Date(profile.created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : null;

  const coverSrc = form.cover_url ? (form.cover_url.startsWith('http') ? form.cover_url : `${process.env.REACT_APP_BACKEND_URL}${form.cover_url}`) : null;
  const avatarSrc = form.avatar_url ? (form.avatar_url.startsWith('http') ? form.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${form.avatar_url}`) : null;

  return (
    <div data-testid="profile-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-[#101828]">Public profile</h2>
          <p className="text-[#475467] text-sm mt-1">How agencies see you. The more complete, the more proposals you&apos;ll get.</p>
        </div>
        {!editing ? (
          <Button onClick={() => setEditing(true)} data-testid="edit-profile-btn"
            className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white">
            <User className="w-4 h-4 mr-2" /> Edit profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditing(false)} data-testid="cancel-profile-btn">Cancel</Button>
            <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn"
              className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        )}
      </div>

      {/* ── Hero card with cover banner + floating avatar ── */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden mb-6">
        {/* Cover */}
        <div className="relative h-44 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 overflow-hidden">
          {coverSrc && <img src={coverSrc} alt="Cover" className="w-full h-full object-cover" />}
          {!coverSrc && (
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: 'radial-gradient(circle at 25% 50%, white 1px, transparent 1px)',
              backgroundSize: '30px 30px',
            }} />
          )}
          {editing && (
            <label className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/90 hover:bg-white text-slate-700 text-xs font-medium cursor-pointer backdrop-blur-sm transition-all shadow">
              <Upload className="w-3.5 h-3.5" /> {uploadingCover ? 'Uploading…' : (form.cover_url ? 'Change cover' : 'Upload cover')}
              <input type="file" accept="image/*" className="hidden" data-testid="upload-cover-input"
                onChange={e => { uploadImage(e.target.files?.[0], 'cover'); e.target.value = ''; }} disabled={uploadingCover} />
            </label>
          )}
        </div>

        <div className="px-8 pb-7 -mt-14 relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            {/* Avatar + name */}
            <div className="flex items-end gap-5">
              <div className="relative">
                <div className="w-28 h-28 rounded-2xl bg-white border-4 border-white shadow-lg overflow-hidden flex items-center justify-center">
                  {avatarSrc ? <img src={avatarSrc} alt={form.display_name} className="w-full h-full object-cover" />
                    : <span className="text-3xl font-semibold text-[#2563EB]">{(form.display_name || '?').slice(0, 1).toUpperCase()}</span>}
                </div>
                {editing && (
                  <label className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white flex items-center justify-center cursor-pointer shadow-md transition-colors"
                    title="Change avatar">
                    <Upload className="w-4 h-4" />
                    <input type="file" accept="image/*" className="hidden" data-testid="upload-avatar-input"
                      onChange={e => { uploadImage(e.target.files?.[0], 'avatar'); e.target.value = ''; }} disabled={uploadingAvatar} />
                  </label>
                )}
              </div>

              <div className="pb-2 flex-1 min-w-0">
                {editing ? (
                  <Input value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})}
                    data-testid="display-name-input" placeholder="Your name"
                    className="text-2xl font-semibold mb-1 max-w-sm" />
                ) : (
                  <h1 className="text-2xl font-semibold text-[#101828]">{form.display_name || 'Unnamed creator'}</h1>
                )}
                {editing ? (
                  <Input value={form.headline} onChange={e => setForm({...form, headline: e.target.value})}
                    data-testid="headline-input" placeholder="A one-line headline (e.g., Fitness + lifestyle creator from Sydney)"
                    className="mt-1 text-sm max-w-md" />
                ) : (
                  form.headline && <p className="text-[#475467] text-sm mt-0.5">{form.headline}</p>
                )}
                <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-[#475467]">
                  <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {profile?.public_id}</span>
                  {memberSince && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Joined {memberSince}</span>}
                  {form.location && !editing && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {form.location}</span>}
                  <StatusBadge status={profile?.verification_status} />
                </div>
              </div>
            </div>

            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(profileUrl); toast.success('Profile link copied'); }}
              data-testid="copy-profile-link-btn" className="self-start md:self-end">
              <Link2 className="w-4 h-4 mr-2" /> Copy link
            </Button>
          </div>
        </div>
      </div>

      {/* ── 2-column body ── */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Bio */}
          <SectionCard>
            <h3 className="text-lg font-semibold text-[#101828] mb-3">About</h3>
            {editing ? (
              <>
                <Textarea value={form.bio} onChange={e => setForm({...form, bio: e.target.value})}
                  data-testid="bio-input" rows={5}
                  placeholder="Tell agencies who you are, what you create, and what makes you a great partner. (3-5 sentences works best.)" />
                <p className="text-[10px] text-[#98A2B3] mt-1">{form.bio.length}/500</p>
              </>
            ) : (
              <p className="text-[#475467] leading-relaxed text-sm whitespace-pre-line">
                {form.bio || <span className="text-[#98A2B3] italic">No bio yet. Click &ldquo;Edit profile&rdquo; to add one.</span>}
              </p>
            )}
          </SectionCard>

          {/* Niches */}
          <SectionCard>
            <h3 className="text-lg font-semibold text-[#101828] mb-1">Categories</h3>
            <p className="text-[#475467] text-xs mb-3">{editing ? 'Pick up to 5 niches that best describe your content.' : 'Areas this creator works in.'}</p>
            {editing ? (
              <div className="flex flex-wrap gap-2">
                {NICHE_OPTIONS.map(n => {
                  const active = form.niches.includes(n);
                  const atLimit = form.niches.length >= 5 && !active;
                  return (
                    <button key={n} type="button" disabled={atLimit}
                      onClick={() => toggleArray('niches', n)}
                      data-testid={`niche-${n.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all
                        ${active ? 'bg-blue-600 text-white border border-blue-600' :
                          atLimit ? 'bg-slate-50 text-slate-300 border border-slate-100 cursor-not-allowed' :
                          'bg-white border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700'}`}>
                      {n}
                    </button>
                  );
                })}
              </div>
            ) : (
              form.niches.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {form.niches.map(n => (
                    <span key={n} className="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium">{n}</span>
                  ))}
                </div>
              ) : <p className="text-[#98A2B3] text-sm italic">None selected yet.</p>
            )}
          </SectionCard>

          {/* Languages */}
          <SectionCard>
            <h3 className="text-lg font-semibold text-[#101828] mb-3">Languages</h3>
            {editing ? (
              <div className="flex flex-wrap gap-2">
                {LANG_OPTIONS.map(l => {
                  const active = form.languages.includes(l);
                  return (
                    <button key={l} type="button" onClick={() => toggleArray('languages', l)}
                      data-testid={`lang-${l.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all
                        ${active ? 'bg-purple-600 text-white border border-purple-600' :
                          'bg-white border border-slate-200 text-slate-600 hover:border-purple-300 hover:text-purple-700'}`}>
                      {l}
                    </button>
                  );
                })}
              </div>
            ) : (
              form.languages.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {form.languages.map(l => (
                    <span key={l} className="px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">{l}</span>
                  ))}
                </div>
              ) : <p className="text-[#98A2B3] text-sm italic">No languages added.</p>
            )}
          </SectionCard>

          {/* Social Links */}
          <SectionCard>
            <h3 className="text-lg font-semibold text-[#101828] mb-3">Social links</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { key: 'instagram', label: 'Instagram', placeholder: '@username' },
                { key: 'twitter',   label: 'X / Twitter', placeholder: '@username' },
                { key: 'youtube',   label: 'YouTube', placeholder: 'channel URL' },
                { key: 'tiktok',    label: 'TikTok', placeholder: '@username' },
                { key: 'website',   label: 'Website', placeholder: 'https://...' },
              ].map(s => (
                <div key={s.key}>
                  <Label className="text-[11px] text-[#475467] font-medium mb-1 block">{s.label}</Label>
                  {editing ? (
                    <Input value={form[s.key]} onChange={e => setForm({...form, [s.key]: e.target.value})}
                      data-testid={`social-${s.key}-input`} placeholder={s.placeholder} className="text-sm" />
                  ) : (
                    form[s.key]
                      ? <a href={form[s.key].startsWith('http') ? form[s.key] : `https://${form[s.key].replace(/^@/, '')}`}
                          target="_blank" rel="noreferrer"
                          className="text-blue-600 hover:underline text-sm inline-flex items-center gap-1">
                          {form[s.key]} <ExternalLink className="w-3 h-3" />
                        </a>
                      : <p className="text-[#98A2B3] text-sm italic">Not added</p>
                  )}
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* Side column */}
        <div className="space-y-6">
          {editing && (
            <SectionCard>
              <h3 className="text-lg font-semibold text-[#101828] mb-3">Quick details</h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-[11px] text-[#475467] font-medium mb-1 block">Location</Label>
                  <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})}
                    data-testid="location-input" placeholder="City, Country" />
                </div>
                <div>
                  <Label className="text-[11px] text-[#475467] font-medium mb-1 block">Pronouns</Label>
                  <Input value={form.pronouns} onChange={e => setForm({...form, pronouns: e.target.value})}
                    data-testid="pronouns-input" placeholder="she/her, he/him, they/them, …" />
                </div>
              </div>
            </SectionCard>
          )}

          {!editing && (form.location || form.pronouns) && (
            <SectionCard>
              <h3 className="text-lg font-semibold text-[#101828] mb-3">Details</h3>
              <dl className="space-y-2 text-sm">
                {form.location && (
                  <div className="flex justify-between gap-3"><dt className="text-[#475467]">Location</dt><dd className="text-[#101828] font-medium">{form.location}</dd></div>
                )}
                {form.pronouns && (
                  <div className="flex justify-between gap-3"><dt className="text-[#475467]">Pronouns</dt><dd className="text-[#101828] font-medium">{form.pronouns}</dd></div>
                )}
              </dl>
            </SectionCard>
          )}

          {/* Profile completeness */}
          <SectionCard>
            <h3 className="text-lg font-semibold text-[#101828] mb-2">Profile strength</h3>
            <ProfileStrengthMeter form={form} />
          </SectionCard>

          {/* Public link card */}
          <SectionCard>
            <h3 className="text-lg font-semibold text-[#101828] mb-1">Share your profile</h3>
            <p className="text-[#475467] text-xs mb-3">Share this link with agencies or on socials to attract proposals.</p>
            <div className="flex items-center gap-2 p-2.5 bg-[#F6F8FC] rounded-lg border border-[#E5E7EB]">
              <span className="text-xs text-[#475467] truncate flex-1">{profileUrl}</span>
              <button onClick={() => { navigator.clipboard.writeText(profileUrl); toast.success('Copied!'); }}
                data-testid="copy-profile-link-sidebar"
                className="text-blue-600 hover:text-blue-800 flex-shrink-0">
                <Link2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

function ProfileStrengthMeter({ form }) {
  const checks = [
    { ok: !!form.avatar_url, label: 'Avatar' },
    { ok: !!form.cover_url, label: 'Cover image' },
    { ok: !!form.headline, label: 'Headline' },
    { ok: form.bio.length >= 50, label: 'Bio (50+ chars)' },
    { ok: form.niches.length > 0, label: 'Categories' },
    { ok: form.languages.length > 0, label: 'Languages' },
    { ok: !!form.location, label: 'Location' },
    { ok: !!(form.instagram || form.twitter || form.youtube || form.tiktok || form.website), label: 'At least 1 social link' },
  ];
  const done = checks.filter(c => c.ok).length;
  const pct = Math.round((done / checks.length) * 100);
  const color = pct >= 80 ? 'emerald' : pct >= 50 ? 'blue' : 'amber';
  const colorClasses = {
    emerald: 'bg-emerald-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
  };
  return (
    <div data-testid="profile-strength">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs text-[#475467]">{done} of {checks.length} complete</span>
        <span className={`text-base font-semibold ${color === 'emerald' ? 'text-emerald-600' : color === 'blue' ? 'text-blue-600' : 'text-amber-600'}`}>{pct}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
        <div className={`h-full ${colorClasses[color]} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <ul className="space-y-1">
        {checks.map(c => (
          <li key={c.label} className="flex items-center gap-2 text-xs">
            {c.ok ? <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" /> : <Clock className="w-3 h-3 text-slate-300 flex-shrink-0" />}
            <span className={c.ok ? 'text-[#101828]' : 'text-[#98A2B3]'}>{c.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PROPOSALS VIEW
   ═══════════════════════════════════════════════════════════ */

function ProposalsView() {
  const [proposals, setProposals] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProposals = useCallback(async () => {
    try {
      const res = await API.get('/creator/proposals');
      setProposals(res.data.proposals);
    } catch { toast.error('Failed to load proposals'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProposals(); }, [fetchProposals]);

  const filtered = filter === 'all' ? proposals : proposals.filter(p => {
    if (filter === 'pending') return p.proposal_status === 'sent';
    if (filter === 'countered') return p.proposal_status === 'countered';
    if (filter === 'accepted') return p.proposal_status === 'accepted';
    if (filter === 'rejected') return p.proposal_status === 'rejected';
    return true;
  });

  const counts = {
    all: proposals.length,
    pending: proposals.filter(p => p.proposal_status === 'sent').length,
    countered: proposals.filter(p => p.proposal_status === 'countered').length,
    accepted: proposals.filter(p => p.proposal_status === 'accepted').length,
    rejected: proposals.filter(p => p.proposal_status === 'rejected').length,
  };

  if (selectedDeal) return <ProposalDetail dealId={selectedDeal} onBack={() => { setSelectedDeal(null); fetchProposals(); }} />;

  return (
    <div data-testid="proposals-panel">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-[#101828]">Proposals</h2>
        <p className="text-[#475467] text-sm mt-1">Review offers from agencies and brands</p>
      </div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {[{ id: 'all', label: 'All' }, { id: 'pending', label: 'Pending' }, { id: 'countered', label: 'Countered' }, { id: 'accepted', label: 'Accepted' }, { id: 'rejected', label: 'Declined' }].map(t => (
          <button key={t.id} onClick={() => setFilter(t.id)} data-testid={`proposals-filter-${t.id}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${filter === t.id ? 'bg-[#2563EB] text-white' : 'bg-white border border-[#E5E7EB] text-[#475467] hover:bg-[#F6F8FC]'}`}>
            {t.label} ({counts[t.id]})
          </button>
        ))}
      </div>
      {loading ? <div className="text-center py-16 text-[#475467]">Loading...</div> : filtered.length === 0 ? (
        <SectionCard className="text-center py-16"><FileText className="w-12 h-12 text-[#D0D5DD] mx-auto mb-4" /><p className="text-[#475467]">No proposals in this category</p></SectionCard>
      ) : (
        <div className="space-y-4">
          {filtered.map(p => {
            const proposal = p.proposal || {};
            const co = proposal.commercial_offer || {};
            const rt = proposal.rights_terms || {};
            const amount = co.amount || proposal.offered_price || 0;
            return (
              <button key={p.id} onClick={() => setSelectedDeal(p.id)} data-testid={`proposal-card-${p.id}`}
                className="w-full text-left bg-white rounded-2xl border border-[#E5E7EB] p-6 hover:shadow-md hover:border-blue-200 transition-all">
                <div className="flex items-start justify-between mb-3 gap-4">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    {/* Agency logo */}
                    <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {p.agency?.logo_url ? (
                        <img src={p.agency.logo_url.startsWith('http') ? p.agency.logo_url : `${process.env.REACT_APP_BACKEND_URL}${p.agency.logo_url}`}
                             alt={p.agency.name} className="w-full h-full object-cover" />
                      ) : (
                        <Briefcase className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="text-[#101828] font-semibold text-lg truncate">{p.campaign?.brand || p.campaign?.name || 'Untitled'}</h3>
                        <StatusBadge status={p.proposal_status} />
                        {p.is_example && (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]" data-testid={`proposal-example-badge-${p.id}`}>
                            Example
                          </Badge>
                        )}
                        {p.agency?.verified_status === 'verified' && (
                          <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] gap-0.5">
                            <Shield className="w-2.5 h-2.5" />Verified
                          </Badge>
                        )}
                      </div>
                      <p className="text-[#475467] text-sm">
                        <span className="font-medium text-[#344054]">{p.agency?.name || 'Unknown Agency'}</span>
                        {p.campaign?.name && p.campaign?.brand && <> · {p.campaign.name}</>}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-2xl font-semibold text-[#2563EB]">${amount.toLocaleString()}</p>
                    <p className="text-[#475467] text-xs">{co.payment_model?.replace('_', ' ') || co.deal_structure?.replace('_', ' ') || 'one-off'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm p-3 bg-[#F6F8FC] rounded-xl">
                  <div><span className="text-[#475467] text-xs block">Platforms</span><span className="text-[#101828] font-medium">{(rt.platforms || proposal.platforms || []).slice(0, 3).join(', ') || 'N/A'}</span></div>
                  <div><span className="text-[#475467] text-xs block">Territory</span><span className="text-[#101828] font-medium">{(rt.territory || proposal.territory || []).slice(0, 2).join(', ') || 'N/A'}</span></div>
                  <div><span className="text-[#475467] text-xs block">Exclusivity</span><span className="text-[#101828] font-medium capitalize">{rt.exclusivity ? rt.exclusivity.replace(/_/g, ' ') : 'Not specified'}</span></div>
                  <div><span className="text-[#475467] text-xs block">Duration</span><span className="text-[#101828] font-medium">{rt.term_start && rt.term_end ? `${rt.term_start} - ${rt.term_end}` : 'TBD'}</span></div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E5E7EB]">
                  <span className="text-[#475467] text-xs">{proposal.sent_at ? `Received ${new Date(proposal.sent_at).toLocaleDateString()}` : ''}</span>
                  <span className="text-[#2563EB] text-sm font-medium flex items-center gap-1">View Details <ChevronRight className="w-4 h-4" /></span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PROPOSAL DETAIL
   ═══════════════════════════════════════════════════════════ */

function ProposalDetail({ dealId, onBack }) {
  const [deal, setDeal] = useState(null);
  const load = () => API.get(`/creator/proposals/${dealId}`).then(r => setDeal(r.data.proposal)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, [dealId]);
  if (!deal) return <div className="text-center py-16 text-slate-500">Loading...</div>;
  return <CreatorProposalDetail deal={deal} onBack={onBack} onRefresh={() => { load(); }} />;
}

/* ═══════════════════════════════════════════════════════════
   ENGAGEMENTS VIEW
   ═══════════════════════════════════════════════════════════ */

function EngagementsView() {
  const [engagements, setEngagements] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const res = await API.get('/creator/engagements');
      setEngagements(res.data.engagements);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (selectedDeal) return <EngagementWorkspace dealId={selectedDeal} onBack={() => { setSelectedDeal(null); fetch(); }} />;

  return (
    <div data-testid="engagements-panel">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-[#101828]">Engagements</h2>
        <p className="text-[#475467] text-sm mt-1">Your active deals and collaborations</p>
      </div>
      {loading ? <div className="text-center py-16 text-[#475467]">Loading...</div> : engagements.length === 0 ? (
        <SectionCard className="text-center py-16"><Handshake className="w-12 h-12 text-[#D0D5DD] mx-auto mb-4" /><p className="text-[#475467]">No active engagements yet</p><p className="text-[#475467] text-sm mt-1">Accept a proposal to start a collaboration</p></SectionCard>
      ) : (
        <div className="space-y-4">
          {engagements.map(eng => {
            const proposal = eng.proposal || {};
            const co = proposal.commercial_offer || {};
            const amount = co.amount || proposal.offered_price || 0;
            return (
              <button key={eng.id} onClick={() => setSelectedDeal(eng.id)} data-testid={`engagement-card-${eng.id}`}
                className="w-full text-left bg-white rounded-2xl border border-[#E5E7EB] p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-[#101828] font-semibold text-lg">{eng.campaign?.brand || eng.campaign?.name}</h3>
                      <StatusBadge status={eng.status} />
                      <StatusBadge status={eng.payment_status} />
                    </div>
                    <p className="text-[#475467] text-sm">{eng.agency?.name} &middot; {eng.campaign?.name}</p>
                  </div>
                  <p className="text-2xl font-semibold text-[#2563EB]">${amount.toLocaleString()}</p>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="p-3 bg-[#F6F8FC] rounded-xl text-center">
                    <p className="text-[#101828] font-semibold">{eng.submission_count || 0}</p>
                    <p className="text-[#475467] text-xs">Content Items</p>
                  </div>
                  <div className="p-3 bg-[#F6F8FC] rounded-xl text-center">
                    <p className={`font-semibold ${eng.pending_review_count > 0 ? 'text-amber-600' : 'text-[#101828]'}`}>{eng.pending_review_count || 0}</p>
                    <p className="text-[#475467] text-xs">Needs Review</p>
                  </div>
                  <div className="p-3 bg-[#F6F8FC] rounded-xl text-center">
                    <p className="text-[#101828] font-semibold">{eng.likeness_pack_count || 0}</p>
                    <p className="text-[#475467] text-xs">Likeness Items</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ENGAGEMENT WORKSPACE
   ═══════════════════════════════════════════════════════════ */

function EngagementWorkspace({ dealId, onBack }) {
  const [engagement, setEngagement] = useState(null);
  const [activeTab, setActiveTab] = useState('agreement');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [diffViewerOpen, setDiffViewerOpen] = useState(false);

  const fetchEngagement = useCallback(async () => {
    try {
      const res = await API.get(`/creator/engagements/${dealId}`);
      setEngagement(res.data.engagement);
    } catch { toast.error('Failed to load'); }
  }, [dealId]);

  useEffect(() => { fetchEngagement(); }, [fetchEngagement]);

  if (!engagement) return <div className="text-center py-16 text-[#475467]">Loading...</div>;

  const proposal = engagement.proposal || {};
  const co = proposal.commercial_offer || {};
  const amount = co.amount || proposal.offered_price || 0;
  const versions = engagement.proposal_versions || [];

  const downloadAgreementPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await API.get(`/deals/${engagement.id}/agreement.pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers?.['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/i.exec(cd);
      link.download = match?.[1] || `mylikeness-agreement-${engagement.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Agreement downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const tabs = [
    { id: 'agreement', label: 'Agreement' },
    { id: 'likeness', label: 'Likeness Pack' },
    { id: 'content', label: 'Content Review' },
    { id: 'payouts', label: 'Payouts' },
  ];

  return (
    <div data-testid="engagement-workspace">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="flex items-center gap-2 text-[#475467] hover:text-[#101828] text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to Engagements
        </button>
        <div className="flex items-center gap-2">
          {versions.length >= 2 && (
            <Button variant="outline" size="sm" onClick={() => setDiffViewerOpen(true)}
              data-testid="open-version-diff-btn"
              className="text-xs border-slate-200 text-slate-700 hover:bg-slate-50 h-8">
              <GitCompare className="w-3.5 h-3.5 mr-1.5" /> Compare versions
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={downloadAgreementPdf} disabled={downloadingPdf}
            data-testid="download-agreement-pdf-btn"
            className="text-xs border-blue-200 text-blue-700 hover:bg-blue-50 h-8">
            <Download className="w-3.5 h-3.5 mr-1.5" />
            {downloadingPdf ? 'Preparing…' : 'Download PDF'}
          </Button>
        </div>
      </div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-[#101828]">{engagement.campaign?.brand || engagement.campaign?.name}</h2>
            <StatusBadge status={engagement.status} />
            {engagement.campaign?.subscription_enabled && <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Subscription</Badge>}
            {engagement.campaign?.royalty_enabled && <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]">Royalty</Badge>}
          </div>
          <p className="text-[#475467] mt-1">{engagement.agency?.name} &middot; {engagement.campaign?.name}</p>
          {engagement.campaign?.pricing_method && engagement.campaign.pricing_method !== 'fixed_per_submission' && (
            <p className="text-[#475467] text-xs mt-0.5 capitalize">Pricing: {engagement.campaign.pricing_method.replace(/_/g, ' ')}</p>
          )}
        </div>
        <p className="text-2xl font-semibold text-[#2563EB]">${amount.toLocaleString()}</p>
      </div>

      <div className="flex gap-1 mb-6 bg-[#F6F8FC] p-1 rounded-xl">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`eng-tab-${t.id}`}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === t.id ? 'bg-white text-[#101828] shadow-sm' : 'text-[#475467] hover:text-[#101828]'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'agreement' && <AgreementTab engagement={engagement} onRefresh={fetchEngagement} />}
      {activeTab === 'likeness' && <LikenessTab engagement={engagement} onRefresh={fetchEngagement} />}
      {activeTab === 'content' && <ContentReviewTab engagement={engagement} onRefresh={fetchEngagement} />}
      {activeTab === 'payouts' && <EngPayoutsTab engagement={engagement} onRefresh={fetchEngagement} />}

      <ProposalDiffViewer
        open={diffViewerOpen}
        onClose={() => setDiffViewerOpen(false)}
        versions={versions}
        currentProposal={proposal}
      />
    </div>
  );
}

function AgreementTab({ engagement, onRefresh }) {
  const proposal = engagement.proposal || {};
  const rt = proposal.rights_terms || {};
  const co = proposal.commercial_offer || {};
  const amount = co.amount || proposal.offered_price || 0;
  const campaign = engagement.campaign || {};
  const [showAmendment, setShowAmendment] = useState(false);
  const [amendForm, setAmendForm] = useState({ amendment_type: 'adjust_terms', description: '', requested_price: '' });
  const [saving, setSaving] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);

  const requestAmendment = async () => {
    if (!amendForm.description.trim()) { toast.error('Please describe the change you want'); return; }
    setSaving(true);
    try {
      await API.post(`/creator/engagements/${engagement.id}/request-amendment`, {
        amendment_type: amendForm.amendment_type,
        description: amendForm.description,
        requested_price: amendForm.requested_price ? parseFloat(amendForm.requested_price) : null,
      });
      toast.success('Amendment request sent');
      setShowAmendment(false);
      setAmendForm({ amendment_type: 'adjust_terms', description: '', requested_price: '' });
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const respondAmendment = async (amendmentId, action) => {
    setRespondingTo(amendmentId);
    try {
      await API.put(`/creator/engagements/${engagement.id}/amendments/${amendmentId}/respond`, { action });
      toast.success(action === 'accept' ? 'Amendment accepted' : 'Amendment declined');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setRespondingTo(null); }
  };

  const pendingAmendments = (engagement.amendments || []).filter(a => a.status === 'proposed' && a.proposed_by_role !== 'creator');

  return (
    <div className="space-y-6" data-testid="agreement-tab">
      {/* Pending Amendments Needing Response */}
      {pendingAmendments.length > 0 && (
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" /> Pending Amendment Requests ({pendingAmendments.length})
          </h3>
          <div className="space-y-3">
            {pendingAmendments.map(a => (
              <div key={a.id} className="p-4 border border-amber-200 bg-amber-50/50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium capitalize text-[#101828]">{a.amendment_type?.replace(/_/g, ' ')}</span>
                  <StatusBadge status={a.status} />
                </div>
                <p className="text-[#475467] text-sm mb-2">{a.description}</p>
                {a.additional_amount > 0 && <p className="text-[#101828] font-semibold text-sm mb-3">+${a.additional_amount.toLocaleString()}</p>}
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => respondAmendment(a.id, 'accept')} disabled={respondingTo === a.id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <CheckCircle className="w-3 h-3 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => respondAmendment(a.id, 'reject')} disabled={respondingTo === a.id}
                    className="border-red-200 text-red-600 hover:bg-red-50">
                    <XCircle className="w-3 h-3 mr-1" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Finalized Terms */}
      <SectionCard>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#101828]">Agreement Terms</h3>
          <Button size="sm" variant="outline" onClick={() => setShowAmendment(true)}
            className="border-purple-200 text-purple-600 hover:bg-purple-50 text-xs"
            data-testid="request-amendment-btn">
            <MessageSquare className="w-3 h-3 mr-1" /> Request Change
          </Button>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]"><span className="text-[#475467]">Deal Value</span><span className="text-[#101828] font-semibold">${amount.toLocaleString()}</span></div>
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]"><span className="text-[#475467]">License Type</span><span className="text-[#101828] font-medium capitalize">{rt.license_type?.replace('_', ' ')}</span></div>
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]"><span className="text-[#475467]">Duration</span><span className="text-[#101828] font-medium">{rt.term_start} &ndash; {rt.term_end}</span></div>
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]"><span className="text-[#475467]">Usage Scope</span><span className="text-[#101828] font-medium capitalize">{rt.usage_scope?.replace('_', ' ') || '—'}</span></div>
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]"><span className="text-[#475467]">Approval Required</span><span className={rt.approval_required ? 'text-emerald-600 font-medium' : 'text-amber-600 font-medium'}>{rt.approval_required ? 'Yes' : 'No'}</span></div>
            <div className="flex justify-between py-2"><span className="text-[#475467]">Endorsement</span><span className={`font-medium ${rt.endorsement_requested ? 'text-amber-600' : 'text-[#101828]'}`}>{rt.endorsement_requested ? 'Enabled' : 'Not included'}</span></div>
          </div>
          <div className="space-y-3 text-sm">
            <div><Label className="text-[#475467] text-xs">Platforms</Label><div className="flex flex-wrap gap-1 mt-1">{(rt.platforms || []).map(p => <Badge key={p} variant="outline" className="text-xs">{p}</Badge>)}</div></div>
            <div><Label className="text-[#475467] text-xs">Territory</Label><div className="flex flex-wrap gap-1 mt-1">{(rt.territory || []).map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}</div></div>
            {rt.restrictions && <div><Label className="text-[#475467] text-xs">Restrictions</Label><p className="text-[#101828] text-sm mt-1">{rt.restrictions}</p></div>}
            {rt.plain_language_summary && <div className="p-3 bg-[#F6F8FC] rounded-xl mt-3"><p className="text-[#475467] text-xs mb-1">Summary</p><p className="text-[#101828] text-sm">{rt.plain_language_summary}</p></div>}
          </div>
        </div>
      </SectionCard>

      {/* Pricing Method */}
      {campaign.pricing_method && campaign.pricing_method !== 'fixed_per_submission' && (
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-3 flex items-center gap-2"><DollarSign className="w-5 h-5 text-blue-600" /> Pricing Method</h3>
          <p className="text-sm text-[#101828] font-medium capitalize mb-2">{campaign.pricing_method.replace(/_/g, ' ')}</p>
          {campaign.rate_card && Object.keys(campaign.rate_card).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(campaign.rate_card).filter(([,v]) => v).map(([k,v]) => (
                <span key={k} className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-lg border border-blue-100 capitalize">{k.replace(/_/g, ' ')}: ${v}</span>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* Subscription Info */}
      {campaign.subscription_enabled && campaign.subscription_terms && (
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-3 flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-600" /> Subscription Agreement</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]"><span className="text-[#475467]">Recurring Price</span><span className="text-emerald-700 font-semibold">${campaign.subscription_terms.recurring_price} / {campaign.subscription_terms.billing_frequency}</span></div>
            {campaign.subscription_terms.included_usage_scope && <div className="flex justify-between py-2 border-b border-[#E5E7EB]"><span className="text-[#475467]">Included Scope</span><span className="text-[#101828]">{campaign.subscription_terms.included_usage_scope}</span></div>}
            <div className="flex justify-between py-2"><span className="text-[#475467]">Auto-Renew</span><span className="text-[#101828] font-medium">{campaign.subscription_terms.auto_renew ? 'Yes' : 'No'}</span></div>
          </div>
        </SectionCard>
      )}

      {/* Royalty Info */}
      {campaign.royalty_enabled && campaign.royalty_terms && (
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-amber-600" /> Royalty Agreement</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]"><span className="text-[#475467]">Royalty Type</span><span className="text-[#101828] font-medium capitalize">{campaign.royalty_terms.royalty_type?.replace(/_/g, ' ')}</span></div>
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]"><span className="text-[#475467]">Royalty Rate</span><span className="text-amber-700 font-semibold">{campaign.royalty_terms.royalty_rate}%</span></div>
            {campaign.royalty_terms.calculation_basis && <div className="flex justify-between py-2 border-b border-[#E5E7EB]"><span className="text-[#475467]">Based On</span><span className="text-[#101828]">{campaign.royalty_terms.calculation_basis}</span></div>}
            {campaign.royalty_terms.notes && <div className="flex justify-between py-2"><span className="text-[#475467]">Notes</span><span className="text-[#101828]">{campaign.royalty_terms.notes}</span></div>}
          </div>
        </SectionCard>
      )}

      {/* License */}
      {engagement.license_id && (
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-2">License</h3>
          <p className="text-[#475467] text-sm">License ID: <code className="bg-[#F6F8FC] px-2 py-1 rounded text-[#2563EB] font-mono">{engagement.license_id}</code></p>
        </SectionCard>
      )}

      {/* Amendment History */}
      {engagement.amendments?.length > 0 && (
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-4">Amendment History</h3>
          <div className="space-y-3">{engagement.amendments.map(a => (
            <div key={a.id} className="p-4 bg-[#F6F8FC] rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium capitalize text-[#101828]">{a.amendment_type?.replace(/_/g, ' ')}</span>
                  {a.proposed_by_role === 'creator' && <Badge variant="outline" className="text-[9px] text-purple-600 border-purple-200">Your request</Badge>}
                </div>
                <StatusBadge status={a.status} />
              </div>
              <p className="text-[#475467] text-sm">{a.description}</p>
              {a.additional_amount > 0 && <p className="text-[#101828] font-semibold text-sm mt-1">+${a.additional_amount.toLocaleString()}</p>}
            </div>
          ))}</div>
        </SectionCard>
      )}

      {/* Request Amendment Dialog */}
      <Dialog open={showAmendment} onOpenChange={setShowAmendment}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Request Term Change</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Change Type</Label>
              <Select value={amendForm.amendment_type} onValueChange={v => setAmendForm(p => ({...p, amendment_type: v}))}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="adjust_terms">Adjust Terms</SelectItem>
                  <SelectItem value="adjust_payment">Adjust Payment / Pricing</SelectItem>
                  <SelectItem value="extend_duration">Extend Duration</SelectItem>
                  <SelectItem value="add_platforms">Add Platforms</SelectItem>
                  <SelectItem value="expand_territory">Expand Territory</SelectItem>
                  <SelectItem value="reduce_scope">Reduce Scope</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Describe the change you want</Label>
              <Textarea value={amendForm.description} onChange={e => setAmendForm(p => ({...p, description: e.target.value}))}
                rows={3} placeholder="I'd like to adjust the territory to include EU markets..." />
            </div>
            {(amendForm.amendment_type === 'adjust_payment' || amendForm.amendment_type === 'extend_duration') && (
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Requested Price Change ($)</Label>
                <Input type="number" value={amendForm.requested_price} onChange={e => setAmendForm(p => ({...p, requested_price: e.target.value}))}
                  placeholder="Leave blank if no price change" />
              </div>
            )}
            <Button onClick={requestAmendment} disabled={saving} className="w-full bg-purple-600 hover:bg-purple-700 text-white">
              <Send className="w-4 h-4 mr-2" /> {saving ? 'Sending...' : 'Send Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LikenessTab({ engagement, onRefresh }) {
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ asset_type: 'headshot', label: '', url: '', notes: '' });
  const items = engagement.likeness_pack || [];
  const lr = (engagement.proposal || {}).likeness_request || {};

  const addItem = async () => {
    try {
      await API.post(`/creator/engagements/${engagement.id}/likeness-pack`, form);
      toast.success('Item added');
      setAddOpen(false);
      setForm({ asset_type: 'headshot', label: '', url: '', notes: '' });
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const deleteItem = async (itemId) => {
    try {
      await API.delete(`/creator/engagements/${engagement.id}/likeness-pack/${itemId}`);
      toast.success('Item removed');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const typeIcons = { headshot: Image, full_body: Image, voice_sample: Mic, video_clip: Video, document: FileIcon };

  return (
    <div className="space-y-6" data-testid="likeness-tab">
      {lr.standard_pack_included !== false && (
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-3">Standard Pack (Required)</h3>
          <p className="text-[#475467] text-sm mb-3">These items are expected as part of the standard likeness delivery.</p>
          <div className="grid md:grid-cols-2 gap-2">
            {[
              { cat: 'images', item: 'Clear front-facing headshots' }, { cat: 'images', item: 'Side / profile images' },
              { cat: 'images', item: 'Smiling and neutral expressions' }, { cat: 'images', item: 'Full-body images' },
              { cat: 'audio', item: 'Short clean audio clip' }, { cat: 'video', item: 'Short talking video' },
              { cat: 'notes', item: 'Basic creator usage notes' },
            ].map(s => (
              <div key={s.item} className="flex items-center gap-2 p-2 bg-[#F6F8FC] rounded-lg text-sm"><CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" /><span className="text-[#101828]">{s.item}</span></div>
            ))}
          </div>
        </SectionCard>
      )}

      {lr.additional_requirements?.length > 0 && (
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-3">Additional Requirements</h3>
          <div className="flex flex-wrap gap-2">{lr.additional_requirements.map(r => <Badge key={r} variant="outline" className="capitalize">{r.replace('_', ' ')}</Badge>)}</div>
          {lr.custom_notes && <p className="text-[#475467] text-sm mt-3">{lr.custom_notes}</p>}
        </SectionCard>
      )}

      <SectionCard>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#101828]">Submitted Items ({items.length})</h3>
          <Button onClick={() => setAddOpen(true)} className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white" size="sm"><Plus className="w-4 h-4 mr-2" /> Add Item</Button>
        </div>
        {items.length === 0 ? (
          <div className="text-center py-8"><FolderOpen className="w-10 h-10 text-[#D0D5DD] mx-auto mb-3" /><p className="text-[#475467] text-sm">No items submitted yet</p></div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {items.map(item => {
              const Icon = typeIcons[item.asset_type] || FileIcon;
              return (
                <div key={item.id} className="flex items-center gap-3 p-3 bg-[#F6F8FC] rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border border-[#E5E7EB]"><Icon className="w-5 h-5 text-[#475467]" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#101828] text-sm font-medium truncate">{item.label}</p>
                    <p className="text-[#475467] text-xs capitalize">{item.asset_type?.replace('_', ' ')}</p>
                  </div>
                  {item.url && <a href={item.url} target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline"><ExternalLink className="w-4 h-4" /></a>}
                  <button onClick={() => deleteItem(item.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add Likeness Pack Item</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-sm font-medium mb-1.5 block">Type</Label>
              <Select value={form.asset_type} onValueChange={v => setForm({...form, asset_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{['headshot', 'full_body', 'voice_sample', 'video_clip', 'document'].map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm font-medium mb-1.5 block">Label</Label><Input value={form.label} onChange={e => setForm({...form, label: e.target.value})} placeholder="e.g., Front headshot - natural light" /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">URL</Label><Input value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://..." /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Notes (optional)</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
            <Button onClick={addItem} className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white">Add Item</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContentReviewTab({ engagement, onRefresh }) {
  const submissions = engagement.submissions || [];
  const [reviewingId, setReviewingId] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const review = async (subId, action) => {
    setSubmitting(true);
    try {
      await API.put(`/creator/engagements/${engagement.id}/content-review/${subId}`, { action, feedback });
      toast.success(action === 'approve' ? 'Content approved' : 'Changes requested');
      setReviewingId(null);
      setFeedback('');
      onRefresh();
    } catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const pending = submissions.filter(s => s.status === 'pending_review');
  const reviewed = submissions.filter(s => s.status !== 'pending_review');

  return (
    <div className="space-y-6" data-testid="content-review-tab">
      {pending.length > 0 && (
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-4">Needs Your Review ({pending.length})</h3>
          <div className="space-y-3">
            {pending.map(sub => (
              <div key={sub.id} className="p-4 border border-amber-200 bg-amber-50/50 rounded-xl">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-[#101828] font-medium">{sub.title}</p>
                    <p className="text-[#475467] text-sm">{sub.description}</p>
                    {sub.content_type && <Badge variant="outline" className="text-[10px] capitalize mt-1">{sub.content_type.replace(/_/g, ' ')}</Badge>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200">Pending Review</Badge>
                    {(sub.proposed_price || sub.confirmed_price) && (
                      <span className="text-sm font-semibold text-[#101828]">${(sub.confirmed_price || sub.proposed_price)?.toLocaleString()}</span>
                    )}
                  </div>
                </div>
                {sub.content_url && (sub.content_type === 'image' || sub.content_type === 'ai_avatar') && (
                  <img src={sub.content_url} alt={sub.title} data-testid={`content-preview-${sub.id}`}
                    className="rounded-lg border border-slate-200 max-h-56 w-auto object-contain mb-3 bg-white" />
                )}
                {sub.content_url && (sub.content_type === 'video' || sub.content_type === 'short_video' || sub.content_type === 'long_video') && (
                  <video src={sub.content_url} controls data-testid={`content-preview-${sub.id}`}
                    className="rounded-lg border border-slate-200 max-h-56 w-auto mb-3 bg-black" />
                )}
                {sub.content_url && (sub.content_type === 'audio' || sub.content_type === 'voice') && (
                  <audio src={sub.content_url} controls data-testid={`content-preview-${sub.id}`} className="w-full mb-3" />
                )}
                {sub.content_url && <a href={sub.content_url} target="_blank" rel="noreferrer" className="text-[#2563EB] text-sm hover:underline flex items-center gap-1 mb-3"><ExternalLink className="w-3 h-3" /> View Content</a>}
                {sub.intended_usage && <p className="text-[#475467] text-xs mb-2">Intended usage: {sub.intended_usage}</p>}
                {reviewingId === sub.id ? (
                  <div className="space-y-3 mt-3 pt-3 border-t border-amber-200">
                    <Textarea value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Add feedback or notes..." rows={2} />
                    <div className="flex gap-2">
                      <Button onClick={() => review(sub.id, 'approve')} disabled={submitting} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle className="w-4 h-4 mr-1" /> Approve</Button>
                      <Button onClick={() => review(sub.id, 'request_changes')} disabled={submitting} size="sm" variant="outline" className="border-orange-200 text-orange-600 hover:bg-orange-50"><XCircle className="w-4 h-4 mr-1" /> Request Changes</Button>
                      <Button onClick={() => setReviewingId(null)} size="sm" variant="ghost">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <Button onClick={() => setReviewingId(sub.id)} size="sm" className="mt-2 bg-[#2563EB] hover:bg-[#1d4ed8] text-white"><Eye className="w-4 h-4 mr-2" /> Review</Button>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard>
        <h3 className="text-lg font-semibold text-[#101828] mb-4">{pending.length > 0 ? 'Previously Reviewed' : 'Content Submissions'} ({reviewed.length})</h3>
        {reviewed.length === 0 && pending.length === 0 ? (
          <div className="text-center py-8"><Eye className="w-10 h-10 text-[#D0D5DD] mx-auto mb-3" /><p className="text-[#475467] text-sm">No content has been submitted for review yet</p></div>
        ) : (
          <div className="space-y-3">
            {reviewed.map(sub => (
              <div key={sub.id} className="flex items-center gap-4 p-4 bg-[#F6F8FC] rounded-xl">
                <div className="flex-1 min-w-0">
                  <p className="text-[#101828] font-medium text-sm">{sub.title}</p>
                  <p className="text-[#475467] text-xs">
                    {sub.content_type?.replace(/_/g, ' ')} &middot; {new Date(sub.created_at).toLocaleDateString()}
                    {sub.proposed_price ? ` · $${sub.proposed_price.toLocaleString()}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={sub.status} />
                  {sub.payment_status === 'paid' && <Badge className="bg-blue-50 text-blue-700 text-[9px]">Paid</Badge>}
                  {sub.license_status === 'active' && <Badge className="bg-violet-50 text-violet-700 text-[9px]">Licensed</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function EngPayoutsTab({ engagement, onRefresh }) {
  const txns = (engagement.transactions || []).filter(t => t.funding_status !== 'cancelled');
  const [approvingId, setApprovingId] = useState(null);

  const approvePayout = async (txnId) => {
    setApprovingId(txnId);
    try {
      await API.post(`/creator/engagements/${engagement.id}/approve-payout/${txnId}`);
      toast.success('Payout approved and released');
      if (onRefresh) onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setApprovingId(null); }
  };

  const needsAction = txns.filter(t => ['funds_secured', 'in_review', 'awaiting_creator_approval'].includes(t.funding_status));
  const completed = txns.filter(t => !['funds_secured', 'in_review', 'awaiting_creator_approval'].includes(t.funding_status));

  return (
    <div className="space-y-6" data-testid="eng-payouts-tab">
      {/* Transactions needing approval */}
      {needsAction.length > 0 && (
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" /> Awaiting Your Approval ({needsAction.length})
          </h3>
          <p className="text-[#475467] text-sm mb-4">Review and approve these payments to release funds to your account.</p>
          <div className="space-y-3">
            {needsAction.map(txn => (
              <div key={txn.id} className="p-4 border border-amber-200 bg-amber-50/30 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-[#101828] font-semibold text-lg">${txn.net_creator_payout?.toLocaleString() || txn.amount?.toLocaleString()}</p>
                    <p className="text-[#475467] text-xs">
                      {txn.payment_type === 'submission_payment' ? 'Content submission payment' : 'Deal payment'}
                      {txn.platform_fee > 0 && ` · Platform fee: $${txn.platform_fee}`}
                      {txn.created_at && ` · ${new Date(txn.created_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <StatusBadge status={txn.funding_status} />
                </div>
                <Button size="sm" onClick={() => approvePayout(txn.id)} disabled={approvingId === txn.id}
                  data-testid={`approve-payout-${txn.id}`}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle className="w-3 h-3 mr-1" /> {approvingId === txn.id ? 'Approving...' : 'Approve & Release'}
                </Button>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Mediation — creator can request release if agency exceeded agreed terms */}
      <MediationBox dealId={engagement.id} role="creator" fundsHeld={needsAction.length > 0} onChange={onRefresh} />

      {/* Payment History */}
      <SectionCard>
        <h3 className="text-lg font-semibold text-[#101828] mb-4">Payment History</h3>
        {txns.length === 0 ? (
          <div className="text-center py-8"><Wallet className="w-10 h-10 text-[#D0D5DD] mx-auto mb-3" /><p className="text-[#475467] text-sm">No payments recorded for this engagement</p></div>
        ) : (
          <div className="space-y-3">
            {(completed.length > 0 ? completed : txns).map(txn => (
              <div key={txn.id} className="p-4 bg-[#F6F8FC] rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-[#101828] font-medium">${txn.net_creator_payout?.toLocaleString() || txn.amount?.toLocaleString()}</p>
                    <p className="text-[#475467] text-xs">
                      {txn.payment_type === 'submission_payment' ? 'Submission payment' : txn.payment_type?.replace(/_/g, ' ') || 'Payment'}
                      {txn.created_at && ` · ${new Date(txn.created_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <StatusBadge status={txn.funding_status} />
                </div>
                {txn.platform_fee > 0 && <p className="text-[#475467] text-xs">Platform fee: ${txn.platform_fee} · Net: ${txn.net_creator_payout?.toLocaleString()}</p>}
                {txn.dispute_reason && <p className="text-red-600 text-xs mt-1">Dispute: {txn.dispute_reason}</p>}
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   LIKENESS PACK (MASTER LIBRARY)
   ═══════════════════════════════════════════════════════════ */

function LikenessPackView() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await API.get('/creator/master-library');
      setAssets(res.data.assets || []);
    } catch { toast.error('Failed to load library'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  // Detect kind from content-type for the right upload route
  const detectKind = (file) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    if (file.type === 'application/pdf') return 'document';
    return null;
  };

  const ASSET_TYPE_LABELS = {
    image: 'image', video: 'video_clip', audio: 'voice_sample', document: 'document',
  };

  const uploadOne = async (file) => {
    const kind = detectKind(file);
    if (!kind) {
      toast.error(`${file.name} — unsupported file type. Allowed: images, videos, audio, PDFs.`);
      return null;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    try {
      // 1. Upload to object storage
      const upRes = await API.post('/uploads/asset', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // 2. Register in master library
      await API.post('/creator/master-library', {
        asset_type: ASSET_TYPE_LABELS[kind] || kind,
        label: file.name.replace(/\.[^/.]+$/, ''), // strip extension
        url: upRes.data.url,
        notes: '',
        content_type: upRes.data.content_type,
        kind: kind,
      });
      return upRes.data;
    } catch (e) {
      toast.error(`${file.name} — ${e.response?.data?.detail || 'upload failed'}`);
      return null;
    }
  };

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const files = Array.from(fileList);
    let ok = 0;
    for (const f of files) {
      const r = await uploadOne(f);
      if (r) ok += 1;
    }
    if (ok > 0) toast.success(`Uploaded ${ok} of ${files.length} file${files.length === 1 ? '' : 's'}`);
    setUploading(false);
    fetchAssets();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    uploadFiles(e.dataTransfer.files);
  };

  const deleteAsset = async (id) => {
    if (!window.confirm('Remove this asset from your library?')) return;
    try {
      await API.delete(`/creator/master-library/${id}`);
      toast.success('Asset removed');
      fetchAssets();
    } catch { toast.error('Failed to remove'); }
  };

  const renderPreview = (asset) => {
    const isFull = asset.url?.startsWith('http') ? asset.url : `${process.env.REACT_APP_BACKEND_URL}${asset.url}`;
    const ct = asset.content_type || '';
    if (asset.kind === 'image' || ct.startsWith('image/') || ['headshot', 'full_body', 'image'].includes(asset.asset_type)) {
      return <img src={isFull} alt={asset.label} className="w-full h-full object-cover" />;
    }
    if (asset.kind === 'video' || ct.startsWith('video/') || asset.asset_type === 'video_clip') {
      return <video src={isFull} className="w-full h-full object-cover" muted preload="metadata" />;
    }
    if (asset.kind === 'audio' || ct.startsWith('audio/') || asset.asset_type === 'voice_sample') {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
          <Mic className="w-8 h-8 text-purple-500 mb-2" />
          <span className="text-[10px] uppercase tracking-wider text-purple-600 font-semibold">Audio</span>
        </div>
      );
    }
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-slate-50">
        <FileIcon className="w-8 h-8 text-slate-400 mb-2" />
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{asset.asset_type || 'File'}</span>
      </div>
    );
  };

  const groups = [
    { kind: 'image', label: 'Images', icon: Image, types: ['image', 'headshot', 'full_body'] },
    { kind: 'video', label: 'Videos', icon: Video, types: ['video', 'video_clip'] },
    { kind: 'audio', label: 'Audio',  icon: Mic,   types: ['audio', 'voice_sample'] },
    { kind: 'document', label: 'Documents', icon: FileIcon, types: ['document'] },
  ];

  const grouped = groups.map(g => ({
    ...g,
    items: assets.filter(a => g.types.includes(a.asset_type) || g.types.includes(a.kind)),
  }));
  const totalAssets = assets.length;

  return (
    <div data-testid="likeness-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-[#101828]">Likeness Pack</h2>
          <p className="text-[#475467] text-sm mt-1">Your private library of reference assets ({totalAssets} item{totalAssets === 1 ? '' : 's'})</p>
        </div>
      </div>

      {/* Upload zone */}
      <label
        onDragOver={e => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        data-testid="upload-dropzone"
        className={`block mb-6 rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-all
          ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-[#D0D5DD] bg-white hover:border-blue-300 hover:bg-blue-50/30'}
          ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
        <input type="file" multiple accept="image/*,video/*,audio/*,.pdf"
               className="hidden" data-testid="upload-input"
               onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }} disabled={uploading} />
        <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${dragActive ? 'bg-blue-100' : 'bg-[#F6F8FC]'}`}>
          <Upload className={`w-6 h-6 ${dragActive ? 'text-blue-600' : 'text-[#475467]'}`} />
        </div>
        <p className="text-[#101828] font-medium text-sm mb-1">
          {uploading ? 'Uploading…' : dragActive ? 'Drop to upload' : 'Drag files here or click to browse'}
        </p>
        <p className="text-[#475467] text-xs">
          Images (5MB) · Videos (100MB) · Audio (25MB) · PDFs (10MB) — multiple files OK
        </p>
      </label>

      <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl mb-6">
        <p className="text-blue-900 text-sm">
          <strong>Private library.</strong> Assets here are only visible to you. When an agency requests a likeness pack
          for a specific deal, you can choose which items to share.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#475467]">Loading…</div>
      ) : totalAssets === 0 ? (
        <SectionCard className="text-center py-12">
          <FolderOpen className="w-12 h-12 text-[#D0D5DD] mx-auto mb-4" />
          <p className="text-[#101828] font-medium text-sm mb-1">Your library is empty</p>
          <p className="text-[#475467] text-xs">Upload reference photos, clips, and voice samples to streamline future deals.</p>
        </SectionCard>
      ) : (
        <div className="space-y-6">
          {grouped.filter(g => g.items.length > 0).map(g => {
            const GIcon = g.icon;
            return (
              <SectionCard key={g.kind}>
                <h3 className="text-lg font-semibold text-[#101828] mb-4 flex items-center gap-2">
                  <GIcon className="w-5 h-5 text-[#2563EB]" />
                  {g.label}
                  <span className="text-xs text-[#475467] font-normal">({g.items.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {g.items.map(asset => {
                    const isPlayable = ['video', 'audio'].includes(asset.kind) || ['video_clip', 'voice_sample'].includes(asset.asset_type);
                    const fullUrl = asset.url?.startsWith('http') ? asset.url : `${process.env.REACT_APP_BACKEND_URL}${asset.url}`;
                    return (
                      <div key={asset.id} data-testid={`asset-card-${asset.id}`}
                        className="group relative bg-white rounded-xl border border-[#E5E7EB] overflow-hidden hover:shadow-md hover:border-blue-200 transition-all">
                        <div className="aspect-square bg-[#F6F8FC]">{renderPreview(asset)}</div>
                        <div className="p-2.5">
                          <p className="text-xs font-medium text-[#101828] truncate">{asset.label}</p>
                          <p className="text-[10px] text-[#475467] mt-0.5">{new Date(asset.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {asset.url && (
                            <a href={fullUrl} target="_blank" rel="noreferrer"
                              data-testid={`asset-preview-${asset.id}`}
                              className="w-7 h-7 rounded-md bg-white/90 backdrop-blur-sm flex items-center justify-center text-[#475467] hover:text-blue-700 shadow-sm">
                              <Eye className="w-3.5 h-3.5" />
                            </a>
                          )}
                          <button onClick={(e) => { e.preventDefault(); deleteAsset(asset.id); }}
                            data-testid={`asset-delete-${asset.id}`}
                            className="w-7 h-7 rounded-md bg-white/90 backdrop-blur-sm flex items-center justify-center text-rose-500 hover:text-rose-700 shadow-sm">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {isPlayable && (
                          <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-[9px] font-medium uppercase tracking-wider backdrop-blur-sm">
                            {asset.kind === 'audio' || asset.asset_type === 'voice_sample' ? 'Audio' : 'Video'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            );
          })}
        </div>
      )}
    </div>
  );
}


function PayoutsView() {
  const [data, setData] = useState({ transactions: [], summary: {}, royalty_projections: [], active_subscriptions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/creator/payouts').then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, []);

  const s = data.summary || {};
  const royalties = data.royalty_projections || [];
  const subscriptions = data.active_subscriptions || [];
  const totalProjectedRoyalty = royalties.reduce((sum, r) => sum + (r.projected_royalty || 0), 0);

  const statCards = [
    { label: 'Lifetime earned', value: `$${(s.total_earned || 0).toLocaleString()}`, color: 'bg-emerald-50 text-emerald-600', icon: DollarSign, hint: 'Total paid out to your bank' },
    { label: 'Funds secured', value: `$${(s.funds_secured || 0).toLocaleString()}`, color: 'bg-blue-50 text-blue-600', icon: Shield, hint: 'Agency paid, awaiting your approval' },
    { label: 'Ready to release', value: `$${(s.pending_release || 0).toLocaleString()}`, color: 'bg-amber-50 text-amber-600', icon: Wallet, hint: 'You approved — withdraw to bank' },
    { label: 'Projected royalty', value: `$${totalProjectedRoyalty.toLocaleString()}`, color: 'bg-teal-50 text-teal-600', icon: TrendingUp, hint: 'Future income from active deals' },
  ];

  return (
    <div data-testid="payouts-panel">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-[#101828]">Payouts</h2>
        <p className="text-[#475467] text-sm mt-1">Earnings, payment status, and how to withdraw to your bank</p>
      </div>

      {/* Smart banner: always shows current Connect status with a clear action */}
      <PayoutSetupCard />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {statCards.map(c => (
          <div key={c.label} data-testid={`payouts-stat-${c.label.toLowerCase().replace(/ /g,'-')}`} className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <div className={`w-10 h-10 rounded-xl ${c.color} flex items-center justify-center mb-4`}><c.icon className="w-5 h-5" /></div>
            <p className="text-3xl font-semibold text-[#101828]">{c.value}</p>
            <p className="text-[#475467] text-sm mt-1">{c.label}</p>
            <p className="text-[#98A2B3] text-[11px] mt-0.5">{c.hint}</p>
          </div>
        ))}
      </div>

      {/* How payouts work — visual stepper */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 mb-6" data-testid="payouts-how-it-works">
        <h3 className="text-lg font-semibold text-[#101828] mb-1">How you get paid</h3>
        <p className="text-[#475467] text-sm mb-5">Money flow from agency to your bank, step by step.</p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          {[
            { n:1, t:'Proposal accepted', d:'You accept a deal from an agency', icon: CheckCircle, color:'text-emerald-600 bg-emerald-50' },
            { n:2, t:'Agency funds', d:'Money is held safely on Mylikeness', icon: Shield, color:'text-blue-600 bg-blue-50' },
            { n:3, t:'Content review', d:'Agency creates content; you review it', icon: Eye, color:'text-purple-600 bg-purple-50' },
            { n:4, t:'You approve', d:'Approve to release the held funds', icon: Zap, color:'text-amber-600 bg-amber-50' },
            { n:5, t:'Bank deposit', d:'Funds arrive 2-7 days via Stripe', icon: Wallet, color:'text-teal-600 bg-teal-50' },
          ].map((step, i, arr) => (
            <div key={step.n} className="relative">
              <div className="flex flex-col items-center text-center px-2">
                <div className={`w-10 h-10 rounded-xl ${step.color} flex items-center justify-center mb-2`}><step.icon className="w-5 h-5" /></div>
                <p className="text-[10px] text-[#98A2B3] font-mono mb-0.5">STEP {step.n}</p>
                <p className="text-[#101828] font-semibold text-sm">{step.t}</p>
                <p className="text-[#475467] text-xs mt-1 leading-snug">{step.d}</p>
              </div>
              {i < arr.length - 1 && <div className="hidden md:block absolute top-5 -right-1 w-2 h-2 rotate-45 border-t border-r border-[#D0D5DD]" />}
            </div>
          ))}
        </div>
      </div>

      {/* Projected Royalty Earnings */}
      {royalties.length > 0 && (
        <SectionCard>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#101828]">Projected royalty earnings</h3>
            <span className="text-lg font-bold text-amber-600">${totalProjectedRoyalty.toLocaleString()}</span>
          </div>
          <div className="space-y-3">
            {royalties.map((r, i) => (
              <div key={i} className="p-4 bg-amber-50/50 border border-amber-100 rounded-xl">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <p className="text-[#101828] font-medium text-sm">{r.campaign_name}</p>
                    <p className="text-[#475467] text-xs">{r.brand} · {r.agency_name}</p>
                  </div>
                  <p className="text-amber-700 font-semibold">${r.projected_royalty?.toLocaleString()}</p>
                </div>
                <div className="flex gap-3 mt-2 text-[10px] text-amber-600">
                  <span>{r.royalty_rate}% {r.royalty_type?.replace(/_/g, ' ')}</span>
                  {r.calculation_basis && <span>· {r.calculation_basis}</span>}
                  <span>· Deal value: ${r.deal_value?.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Active Subscriptions */}
      {subscriptions.length > 0 && (
        <SectionCard>
          <h3 className="text-lg font-semibold text-[#101828] mb-4">Active subscriptions</h3>
          <div className="space-y-3">
            {subscriptions.map((sub, i) => (
              <div key={i} className="p-4 bg-emerald-50/50 border border-emerald-100 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#101828] font-medium text-sm">{sub.campaign_name}</p>
                    <p className="text-[#475467] text-xs">{sub.brand} · {sub.agency_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-700 font-semibold">${sub.recurring_price} / {sub.billing_frequency}</p>
                    {sub.auto_renew && <p className="text-[10px] text-emerald-500">Auto-renew</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard>
        <h3 className="text-lg font-semibold text-[#101828] mb-4">Transaction history</h3>
        {loading ? <div className="text-center py-8 text-[#475467]">Loading…</div> : data.transactions.length === 0 ? (
          <div className="text-center py-12 px-4" data-testid="payouts-empty-state">
            <div className="w-14 h-14 rounded-2xl bg-[#F6F8FC] flex items-center justify-center mx-auto mb-4">
              <Wallet className="w-7 h-7 text-[#98A2B3]" />
            </div>
            <p className="text-[#101828] font-semibold text-sm mb-1">No transactions yet</p>
            <p className="text-[#475467] text-xs max-w-sm mx-auto">Accept your first proposal from an agency to start earning. Your payment history will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.transactions.map(txn => (
              <div key={txn.id} className="flex items-center gap-4 p-4 bg-[#F6F8FC] rounded-xl">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  txn.funding_status === 'paid_out' ? 'bg-emerald-50' :
                  txn.funding_status === 'ready_for_release' ? 'bg-amber-50' :
                  txn.funding_status === 'disputed' ? 'bg-rose-50' :
                  'bg-blue-50'
                }`}>
                  {txn.funding_status === 'paid_out' ? <CheckCircle className="w-5 h-5 text-emerald-600" /> :
                   txn.funding_status === 'ready_for_release' ? <Wallet className="w-5 h-5 text-amber-600" /> :
                   txn.funding_status === 'disputed' ? <AlertCircle className="w-5 h-5 text-rose-600" /> :
                   <Clock className="w-5 h-5 text-blue-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#101828] font-medium text-sm">{txn.campaign?.brand || txn.campaign?.name || 'Transaction'}</p>
                  <p className="text-[#475467] text-xs">{txn.agency?.name || ''} &middot; {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : ''}</p>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${txn.funding_status === 'paid_out' ? 'text-emerald-600' : 'text-[#101828]'}`}>${txn.net_creator_payout?.toLocaleString()}</p>
                  <StatusBadge status={txn.funding_status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAYOUT SETUP CARD — Always-visible Connect status with action
   Combines what was ConnectAccountBanner but always shows something useful.
   ═══════════════════════════════════════════════════════════ */
function PayoutSetupCard() {
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [country, setCountry] = useState('AU');

  const COUNTRY_LABELS = {
    AU: 'Australia', US: 'United States', GB: 'United Kingdom', NZ: 'New Zealand',
    DE: 'Germany', FR: 'France', ES: 'Spain', IT: 'Italy', NL: 'Netherlands',
    BE: 'Belgium', IE: 'Ireland', AT: 'Austria', PT: 'Portugal', FI: 'Finland',
    GR: 'Greece', CA: 'Canada',
  };

  // Map raw Stripe requirement keys → creator-friendly items to complete.
  const summarizeRequirements = (reqs) => {
    const keys = [...new Set([...(reqs?.currently_due || []), ...(reqs?.past_due || [])])];
    const items = new Set();
    keys.forEach((k) => {
      if (k.startsWith('individual.first_name') || k.startsWith('individual.last_name')) items.add('Your legal name');
      else if (k.startsWith('individual.dob')) items.add('Date of birth');
      else if (k.startsWith('individual.address')) items.add('Home address');
      else if (k === 'individual.phone') items.add('Phone number');
      else if (k === 'individual.email') items.add('Email address');
      else if (k.startsWith('individual.id_number') || k.startsWith('individual.ssn') || k.startsWith('individual.verification')) items.add('Identity verification (ID document)');
      else if (k === 'external_account') items.add('Bank account for payouts');
      else if (k.startsWith('business_profile')) items.add('Basic profile details');
      else if (k.startsWith('tos_acceptance')) items.add("Accept Stripe's terms of service");
      else if (k.startsWith('company') || k.startsWith('representative') || k.startsWith('owners') || k.startsWith('directors')) items.add('Business representative details');
    });
    return [...items];
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        API.get('/connect/creator/status'),
        API.get('/connect/creator/pending-funds').catch(() => ({ data: { total_pending: 0, count: 0 } })),
      ]);
      setStatus(s.data);
      setPending(p.data);
      if (s.data.country) setCountry(s.data.country);
    } catch {
      setStatus({ stripe_configured: false });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startOnboarding = async () => {
    // Open a tab synchronously (inside the click gesture) so it isn't popup-blocked AND
    // so Stripe loads at the top level — Stripe Connect refuses to render inside the
    // preview iframe (frame-ancestors 'none'), which is why an in-page redirect failed.
    const stripeTab = window.open('about:blank', '_blank');
    setWorking(true);
    try {
      const here = window.location.href.split('?')[0];
      const res = await API.post('/connect/creator/start-onboarding', {
        return_url: `${here}?connect=done`,
        refresh_url: `${here}?connect=refresh`,
        country,
      });
      const url = res.data.onboarding_url;
      if (stripeTab && !stripeTab.closed) {
        stripeTab.location.href = url;
        toast.success('Opening Stripe secure setup in a new tab…');
      } else {
        // Popup blocked — fall back to top-level navigation.
        try { (window.top || window).location.href = url; }
        catch { window.location.href = url; }
      }
    } catch (e) {
      if (stripeTab && !stripeTab.closed) stripeTab.close();
      toast.error(e.response?.data?.detail || 'Failed to start onboarding');
    } finally {
      setWorking(false);
    }
  };

  const claimPending = async () => {
    setWorking(true);
    try {
      const res = await API.post('/connect/creator/claim-pending-funds');
      toast.success(res.data.message);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to claim funds');
    } finally { setWorking(false); }
  };

  const openDashboard = async () => {
    setWorking(true);
    try {
      const res = await API.post('/connect/creator/dashboard-link');
      window.open(res.data.dashboard_url, '_blank');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open Stripe dashboard');
    } finally { setWorking(false); }
  };

  if (loading) return <div data-testid="payout-setup-loading" className="h-28 bg-white rounded-2xl border border-[#E5E7EB] mb-6 animate-pulse" />;

  const pendingTotal = pending?.total_pending || 0;
  const pendingCount = pending?.count || 0;
  const pendingCurrency = pending?.currency || 'AUD';
  const supportedCountries = status?.supported_countries || ['AU', 'US', 'GB', 'NZ', 'DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'IE', 'AT', 'PT', 'FI', 'GR', 'CA'];

  // STATE 1 — Verified, no pending
  if (status?.account_exists && status.payouts_enabled && status.charges_enabled && pendingTotal === 0) {
    return (
      <div data-testid="payout-setup-verified" className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-6 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-900 text-sm">Payouts enabled · Connected to Stripe</h3>
            <p className="text-emerald-700 text-xs mt-0.5">
              Future approved payments arrive in your bank account in 2-7 business days. Manage your bank details and view balance in the Stripe Express dashboard.
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={openDashboard} disabled={working}
          data-testid="payout-open-dashboard-btn"
          className="h-9 text-xs bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-100 flex-shrink-0">
          <ExternalLink className="w-3 h-3 mr-1.5" /> Stripe dashboard
        </Button>
      </div>
    );
  }

  // STATE 2 — Verified WITH pending funds → release CTA
  if (status?.payouts_enabled && pendingTotal > 0) {
    return (
      <div data-testid="payout-setup-claim-now"
        className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-6 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-blue-900 text-sm">
              {pendingCurrency} ${pendingTotal.toFixed(2)} ready to release to your bank
            </h3>
            <p className="text-blue-700 text-xs mt-0.5">
              {pendingCount} approved payment{pendingCount > 1 ? 's are' : ' is'} waiting. Click to send to your connected bank account.
            </p>
          </div>
        </div>
        <Button onClick={claimPending} disabled={working}
          data-testid="payout-claim-now-btn"
          className="h-9 text-xs bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0">
          {working ? 'Releasing…' : 'Release now'}
        </Button>
      </div>
    );
  }

  // STATE 3 — Account exists but incomplete (started but not finished)
  if (status?.account_exists) {
    const hasIssues = (status.requirements?.past_due?.length || 0) > 0 || status.requirements?.disabled_reason;
    const neededItems = summarizeRequirements(status.requirements);
    const tone = hasIssues ? { bg: 'rose-50', border: 'rose-200', icon: 'rose-600', iconBg: 'rose-100', title: 'rose-900', text: 'rose-700', btn: 'rose-600 hover:bg-rose-700' }
                           : { bg: 'amber-50', border: 'amber-200', icon: 'amber-600', iconBg: 'amber-100', title: 'amber-900', text: 'amber-700', btn: 'amber-600 hover:bg-amber-700' };
    return (
      <div data-testid="payout-setup-incomplete" className={`bg-${tone.bg} border border-${tone.border} rounded-2xl p-5 mb-6`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl bg-${tone.iconBg} flex items-center justify-center flex-shrink-0`}>
              <AlertCircle className={`w-5 h-5 text-${tone.icon}`} />
            </div>
            <div>
              <h3 className={`font-semibold text-${tone.title} text-sm`}>
                {hasIssues ? 'A few details needed to turn on payouts' : 'Finish your payout setup'}
              </h3>
              <p className={`text-${tone.text} text-xs mt-0.5`}>
                {hasIssues
                  ? "Stripe (our secure payments partner) still needs a bit more information before it can send money to your bank. It takes about 3 minutes — just tap Continue setup below."
                  : pendingTotal > 0
                    ? `You have ${pendingCurrency} $${pendingTotal.toFixed(2)} waiting. Complete setup to claim it.`
                    : "You started Stripe setup but haven't completed it yet. Finish anytime."}
              </p>
            </div>
          </div>
          <Button size="sm" onClick={startOnboarding} disabled={working}
            data-testid="payout-resume-onboarding-btn"
            className={`h-9 text-xs flex-shrink-0 bg-${tone.btn} text-white`}>
            {working ? 'Loading…' : 'Continue setup'}
          </Button>
        </div>
        {neededItems.length > 0 && (
          <div className="mt-3 ml-13" data-testid="payout-needed-items">
            <p className={`text-[11px] font-medium text-${tone.text} mb-1.5`}>What Stripe still needs:</p>
            <div className="flex flex-wrap gap-1.5">
              {neededItems.map((it) => (
                <span key={it} data-testid="payout-needed-chip" className={`text-[11px] px-2 py-0.5 rounded-full bg-${tone.iconBg} text-${tone.title}`}>{it}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // STATE 4 — Has pending funds, no account → urgent CTA
  if (pendingTotal > 0) {
    return (
      <div data-testid="payout-setup-funds-waiting" className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl p-5 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Wallet className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-emerald-900 text-sm">
              {pendingCurrency} ${pendingTotal.toFixed(2)} ready to withdraw{pendingCount > 1 ? ` (${pendingCount} payments)` : ''}
            </h3>
            <p className="text-emerald-700 text-xs mt-0.5">
              Set up your bank account with Stripe Express (5 mins). After that, all future approved payments arrive automatically.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 ml-13">
          <div className="flex-1">
            <Label className="text-[11px] text-emerald-800 font-medium mb-1 block">Country / region</Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="payout-country-select" className="h-9 text-xs bg-white border-emerald-200 max-w-[260px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {supportedCountries.map(c => <SelectItem key={c} value={c}>{COUNTRY_LABELS[c] || c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={startOnboarding} disabled={working}
            data-testid="payout-claim-funds-onboarding-btn"
            className="h-9 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
            {working ? 'Loading…' : <>Set up payouts to claim <ChevronRight className="w-3 h-3 ml-1" /></>}
          </Button>
        </div>
      </div>
    );
  }

  // STATE 5 — Default (no account, no pending) → soft encouragement
  return (
    <div data-testid="payout-setup-not-started" className="bg-white border border-[#E5E7EB] rounded-2xl p-5 mb-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Wallet className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-[#101828] text-sm">Connect a bank account to receive payouts</h3>
          <p className="text-[#475467] text-xs mt-0.5">
            Set up Stripe Express (5 minutes) so approved payments deposit straight into your bank. You can do this now or wait until you earn your first payment — your choice.
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 ml-13">
        <div className="flex-1">
          <Label className="text-[11px] text-[#475467] font-medium mb-1 block">Country / region</Label>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger data-testid="payout-country-select" className="h-9 text-xs bg-white border-[#D0D5DD] max-w-[260px]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {supportedCountries.map(c => <SelectItem key={c} value={c}>{COUNTRY_LABELS[c] || c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={startOnboarding} disabled={working}
          data-testid="payout-start-onboarding-btn"
          className="h-9 text-xs bg-blue-600 hover:bg-blue-700 text-white">
          {working ? 'Loading…' : <>Set up payouts <ChevronRight className="w-3 h-3 ml-1" /></>}
        </Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SETTINGS VIEW
   ═══════════════════════════════════════════════════════════ */

function SettingsView({ onRefresh }) {
  const [settings, setSettings] = useState({ identity: {}, permissions: {}, scope_posture: {} });
  const [identity, setIdentity] = useState({});
  const [permissions, setPermissions] = useState({});
  const [scopePosture, setScopePosture] = useState({});
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState('identity');
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [identityError, setIdentityError] = useState(null);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [identityMatch, setIdentityMatch] = useState(null);

  const loadIdentityStatus = () => {
    API.get('/creator/identity/status').then(r => {
      setVerificationStatus(r.data.verification_status);
      setIdentityError(r.data.last_error || null);
      setIdentityMatch(r.data.identity_match || null);
      // A status refresh may have backfilled name/DOB from the verified ID — reload the card.
      API.get('/creator/settings').then(s => setIdentity(s.data.identity || {})).catch(() => {});
    }).catch(() => {});
  };

  useEffect(() => {
    API.get('/creator/settings').then(r => {
      setSettings(r.data);
      setIdentity(r.data.identity || {});
      setPermissions(r.data.permissions || {});
      setScopePosture(r.data.scope_posture || {});
      setVerificationStatus(r.data.verification_status);
    }).catch(() => {});
    loadIdentityStatus();
    // Refresh when the creator returns from the Stripe Identity tab.
    const onFocus = () => loadIdentityStatus();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const saveIdentity = async () => { setSaving(true); try { await API.put('/creator/identity', identity); toast.success('Identity saved'); onRefresh(); } catch { toast.error('Failed'); } finally { setSaving(false); } };
  const savePermissions = async () => { setSaving(true); try { await API.put('/creator/permissions', permissions); toast.success('Permissions saved'); onRefresh(); } catch { toast.error('Failed'); } finally { setSaving(false); } };
  const saveScopePosture = async () => { setSaving(true); try { await API.put('/creator/scope-posture', scopePosture); toast.success('Scope posture saved'); onRefresh(); } catch { toast.error('Failed'); } finally { setSaving(false); } };

  const startIdentityVerification = async () => {
    // Open a tab synchronously (avoids popup blocking + Stripe can't load in the preview iframe).
    const tab = window.open('about:blank', '_blank');
    setIdentityLoading(true);
    try {
      const res = await API.post('/creator/identity/verify');
      const url = res.data.url;
      if (res.data.status === 'verified') {
        if (tab && !tab.closed) tab.close();
        setVerificationStatus('verified');
        toast.success('Your identity is already verified');
      } else if (url) {
        if (tab && !tab.closed) { tab.location.href = url; toast.success('Opening identity check in a new tab…'); }
        else { try { (window.top || window).location.href = url; } catch { window.location.href = url; } }
      } else {
        if (tab && !tab.closed) tab.close();
        setVerificationStatus(res.data.status);
      }
    } catch (e) {
      if (tab && !tab.closed) tab.close();
      toast.error(e.response?.data?.detail || 'Could not start identity verification');
    } finally { setIdentityLoading(false); }
  };

  const sections = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'identity', label: 'Identity & Verification', icon: Shield },
    { id: 'permissions', label: 'Permissions', icon: Lock },
    { id: 'scope', label: 'Scope Posture', icon: Globe },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: KeyRound },
    { id: 'help', label: 'Help & Support', icon: HelpCircle },
  ];

  return (
    <div data-testid="settings-panel">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-[#101828]">Settings</h2>
        <p className="text-[#475467] text-sm mt-1">Configure your identity, permissions, and licensing posture</p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {sections.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)} data-testid={`settings-section-${s.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${section === s.id ? 'bg-[#2563EB] text-white' : 'bg-white border border-[#E5E7EB] text-[#475467] hover:bg-[#F6F8FC]'}`}>
            <s.icon className="w-4 h-4" />{s.label}
          </button>
        ))}
      </div>

      {section === 'account' && <AccountSection onRefresh={onRefresh} />}

      {section === 'notifications' && <NotificationsSection />}

      {section === 'security' && <SecuritySection />}

      {section === 'help' && <HelpSupportSection />}

      {section === 'identity' && (
        <div className="space-y-6">
          <SectionCard>
            <div className="flex items-center justify-between mb-6">
              <div><h3 className="text-lg font-semibold text-[#101828]">Identity</h3><p className="text-[#475467] text-sm">Private information for verification</p></div>
              <Button onClick={saveIdentity} disabled={saving} className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white"><Save className="w-4 h-4 mr-2" />Save</Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label className="text-sm font-medium mb-1.5 block">Legal Name (private)</Label><Input value={identity.legal_name || ''} onChange={e => setIdentity({...identity, legal_name: e.target.value})} placeholder="Full legal name" /></div>
              <div><Label className="text-sm font-medium mb-1.5 block">Date of Birth (private)</Label><Input type="date" value={identity.date_of_birth || ''} onChange={e => setIdentity({...identity, date_of_birth: e.target.value})} /></div>
              <div><Label className="text-sm font-medium mb-1.5 block">Country</Label>
                <Select value={COUNTRY_CODE_NAMES[identity.country] || identity.country || ''} onValueChange={v => setIdentity({...identity, country: v})}>
                  <SelectTrigger data-testid="identity-country-select"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent className="bg-white">{TERRITORIES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm font-medium mb-1.5 block">Phone (private)</Label><Input value={identity.phone || ''} onChange={e => setIdentity({...identity, phone: e.target.value})} placeholder="+1 234 567 8900" /></div>
            </div>
          </SectionCard>

          <SectionCard data-testid="identity-verification-section">
            <div className="flex items-center justify-between mb-4">
              <div><h3 className="text-lg font-semibold text-[#101828]">Identity Verification</h3><p className="text-[#475467] text-sm">Verify your government ID + a selfie via Stripe</p></div>
              <span data-testid="identity-status-badge"><StatusBadge status={verificationStatus || 'unverified'} /></span>
            </div>
            {verificationStatus === 'verified' ? (
              <div className="space-y-3" data-testid="identity-verified-box">
                <div className="flex items-center gap-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-emerald-600 shrink-0" />
                  <div><p className="text-emerald-800 font-medium">Identity Verified</p><p className="text-emerald-600 text-sm">Your government ID and selfie were confirmed by Stripe.</p></div>
                </div>
                {identityMatch && (
                  identityMatch.all_match ? (
                    <p className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid="identity-match-ok">
                      <CheckCircle className="w-4 h-4" /> Your verified ID matches your account details{identityMatch.dob_match === true ? ' (name & date of birth)' : ' (name)'}.
                    </p>
                  ) : (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 flex items-start gap-2" data-testid="identity-match-warn">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>Heads up: your verified ID{!identityMatch.name_match ? ' name' : ''}{identityMatch.dob_match === false ? (identityMatch.name_match ? ' date of birth' : ' & date of birth') : ''} differs from the details on your account{identityMatch.verified_name ? ` (ID reads "${identityMatch.verified_name}")` : ''}. Please make sure your account details match your legal ID.</span>
                    </div>
                  )
                )}
              </div>
            ) : verificationStatus === 'processing' ? (
              <div className="space-y-3" data-testid="identity-processing-box">
                <div className="flex items-center gap-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <Clock className="w-6 h-6 text-amber-600 shrink-0" />
                  <div><p className="text-amber-800 font-medium">Verifying…</p><p className="text-amber-600 text-sm">Stripe is reviewing your documents — this usually takes a few minutes.</p></div>
                </div>
                <Button variant="outline" onClick={loadIdentityStatus} data-testid="identity-refresh-btn">Refresh status</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-[#F6F8FC] border border-[#E5E7EB] rounded-xl">
                  <Shield className="w-5 h-5 text-[#2563EB] mt-0.5 shrink-0" />
                  <p className="text-sm text-[#475467]">
                    <span className="text-[#101828] font-medium block mb-0.5">Verify with Stripe</span>
                    You'll photograph a government-issued ID (passport, driver's licence, or national ID) and take a quick selfie. Stripe confirms it's really you — usually within minutes. It opens in a new tab.
                  </p>
                </div>
                {identityError && (
                  <p className="text-sm text-red-500 flex items-center gap-1.5" data-testid="identity-error"><AlertCircle className="w-4 h-4" /> Your last attempt couldn't be verified. Please try again.</p>
                )}
                <Button onClick={startIdentityVerification} disabled={identityLoading}
                  className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white" data-testid="start-identity-verification-btn">
                  <Shield className="w-4 h-4 mr-2" />
                  {identityLoading ? 'Starting…' : (verificationStatus === 'requires_input' ? 'Continue verification' : 'Verify my identity')}
                </Button>
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {section === 'permissions' && (
        <SectionCard>
          <div className="flex items-center justify-between mb-6">
            <div><h3 className="text-lg font-semibold text-[#101828]">Permissions</h3><p className="text-[#475467] text-sm">Control what your likeness can be used for</p></div>
            <Button onClick={savePermissions} disabled={saving} className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white"><Save className="w-4 h-4 mr-2" />Save</Button>
          </div>

          <div className="space-y-6">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-center justify-between"><div><p className="text-amber-800 font-medium">Endorsement Policy</p><p className="text-amber-600 text-sm">Off by default. Agencies can request it per deal.</p></div><Badge className="bg-amber-100 text-amber-700 border-amber-200">Off by default</Badge></div>
            </div>

            <div><Label className="text-sm font-medium mb-1.5 block">Approval Mode</Label>
              <Select value={permissions.approval_mode || 'manual'} onValueChange={v => setPermissions({...permissions, approval_mode: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="manual">Manual approval for all</SelectItem><SelectItem value="auto_safe">Auto-approve safe categories</SelectItem></SelectContent>
              </Select>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div><Label className="text-sm font-medium mb-2 block">Allowed Categories</Label>
                <div className="space-y-2 max-h-48 overflow-auto p-3 bg-[#F6F8FC] rounded-xl">
                  {CATEGORIES.map(cat => (<label key={cat} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={permissions.allowed_categories?.includes(cat)} onChange={e => { const cats = permissions.allowed_categories || []; setPermissions({...permissions, allowed_categories: e.target.checked ? [...cats, cat] : cats.filter(c => c !== cat)}); }} className="rounded" /><span>{cat}</span></label>))}
                </div>
              </div>
              <div><Label className="text-sm font-medium mb-2 block flex items-center gap-2"><Ban className="w-4 h-4 text-red-500" /> Banned Categories</Label>
                <div className="space-y-2 max-h-48 overflow-auto p-3 bg-[#F6F8FC] rounded-xl">
                  {['Politics', 'Adult Content', 'Gambling', 'Tobacco', 'Alcohol', 'Firearms', 'Cryptocurrency'].map(cat => (<label key={cat} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={permissions.banned_categories?.includes(cat)} onChange={e => { const cats = permissions.banned_categories || []; setPermissions({...permissions, banned_categories: e.target.checked ? [...cats, cat] : cats.filter(c => c !== cat)}); }} className="rounded" /><span>{cat}</span></label>))}
                </div>
              </div>
            </div>

            <div><Label className="text-sm font-medium mb-2 block">Claims Restrictions</Label>
              <div className="flex flex-wrap gap-2">
                {CLAIMS_RESTRICTIONS.map(claim => (<label key={claim} className="flex items-center gap-2 px-3 py-2 bg-[#F6F8FC] rounded-lg text-sm cursor-pointer hover:bg-[#EEF2FA]"><input type="checkbox" checked={permissions.claims_restrictions?.includes(claim)} onChange={e => { const claims = permissions.claims_restrictions || []; setPermissions({...permissions, claims_restrictions: e.target.checked ? [...claims, claim] : claims.filter(c => c !== claim)}); }} className="rounded" /><span>{claim}</span></label>))}
              </div>
            </div>

            <div><Label className="text-sm font-medium mb-1.5 block">Brand Blacklist</Label><Textarea value={permissions.brand_blacklist?.join(', ') || ''} onChange={e => setPermissions({...permissions, brand_blacklist: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} rows={2} placeholder="Comma-separated brand names" /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Tone Guide</Label><Textarea value={permissions.tone_guide || ''} onChange={e => setPermissions({...permissions, tone_guide: e.target.value})} rows={2} placeholder="How should your persona sound?" /></div>
          </div>
        </SectionCard>
      )}

      {section === 'scope' && (
        <SectionCard>
          <div className="flex items-center justify-between mb-6">
            <div><h3 className="text-lg font-semibold text-[#101828]">Scope Posture</h3><p className="text-[#475467] text-sm">What you typically support (visible on profile)</p></div>
            <Button onClick={saveScopePosture} disabled={saving} className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white"><Save className="w-4 h-4 mr-2" />Save</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div><Label className="text-sm font-medium mb-2 block">Supported Territories</Label>
              <div className="space-y-2 max-h-48 overflow-auto p-3 bg-[#F6F8FC] rounded-xl">
                {TERRITORIES.map(t => (<label key={t} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={scopePosture.supported_territories?.includes(t)} onChange={e => { const terrs = scopePosture.supported_territories || []; setScopePosture({...scopePosture, supported_territories: e.target.checked ? [...terrs, t] : terrs.filter(x => x !== t)}); }} className="rounded" /><span>{t}</span></label>))}
              </div>
            </div>
            <div><Label className="text-sm font-medium mb-2 block">Supported Platforms</Label>
              <div className="space-y-2 max-h-48 overflow-auto p-3 bg-[#F6F8FC] rounded-xl">
                {PLATFORMS.map(p => (<label key={p} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={scopePosture.supported_platforms?.includes(p)} onChange={e => { const plats = scopePosture.supported_platforms || []; setScopePosture({...scopePosture, supported_platforms: e.target.checked ? [...plats, p] : plats.filter(x => x !== p)}); }} className="rounded" /><span>{p}</span></label>))}
              </div>
            </div>
          </div>
          <div className="mt-4"><Label className="text-sm font-medium mb-1.5 block">Typical Term (days)</Label><Input type="number" value={scopePosture.typical_term_days || ''} onChange={e => setScopePosture({...scopePosture, typical_term_days: parseInt(e.target.value) || null})} className="w-32" placeholder="90" /></div>
        </SectionCard>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   ACCOUNT SECTION — Display name, email, avatar, danger zone
   ═══════════════════════════════════════════════════════════ */
function AccountSection({ onRefresh }) {
  const [account, setAccount] = useState({ display_name: '', email: '', bio: '', avatar_url: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    API.get('/auth/me').then(r => {
      const u = r.data.user || r.data;
      setAccount({
        display_name: u.display_name || u.name || '',
        email: u.email || '',
        bio: (u.creator_profile?.bio) || u.bio || '',
        avatar_url: (u.creator_profile?.avatar_url) || u.avatar_url || '',
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await API.patch('/creator/profile', {
        display_name: account.display_name,
        bio: account.bio,
        avatar_url: account.avatar_url,
      });
      toast.success('Account updated');
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await API.post('/uploads/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAccount(a => ({ ...a, avatar_url: res.data.url }));
      // Auto-save the avatar so it's persisted immediately (don't rely on the user pressing Save)
      try {
        await API.patch('/creator/profile', { avatar_url: res.data.url });
        toast.success('Avatar updated');
        onRefresh?.();
      } catch {
        toast.error('Uploaded but failed to save — click Save to persist.');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <div className="space-y-6" data-testid="account-section">
      <SectionCard>
        <div className="flex items-center justify-between mb-6">
          <div><h3 className="text-lg font-semibold text-[#101828]">Account</h3><p className="text-[#475467] text-sm">Your public profile information</p></div>
          <Button onClick={save} disabled={saving} data-testid="save-account-btn" className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white">
            <Save className="w-4 h-4 mr-2" />Save
          </Button>
        </div>

        <div className="flex items-start gap-5 mb-6 pb-6 border-b border-[#E5E7EB]">
          <div className="flex flex-col items-center gap-2">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-200 to-purple-200 overflow-hidden flex items-center justify-center flex-shrink-0">
              {account.avatar_url ? <img src={account.avatar_url} alt="" className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-blue-600" />}
            </div>
            <label className="cursor-pointer">
              <span className="text-[11px] text-blue-600 font-medium hover:underline">{uploading ? 'Uploading...' : 'Change'}</span>
              <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} disabled={uploading} data-testid="avatar-upload-input" />
            </label>
          </div>
          <div className="flex-1 grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Display name</Label>
              <Input data-testid="display-name-input" value={account.display_name} onChange={e => setAccount({...account, display_name: e.target.value})} placeholder="How you appear to agencies" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Email</Label>
              <Input value={account.email} disabled className="bg-[#F6F8FC] text-[#475467]" />
              <p className="text-[10px] text-[#98A2B3] mt-1">Contact support to change your account email.</p>
            </div>
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium mb-1.5 block">Bio</Label>
          <Textarea data-testid="bio-input" value={account.bio} onChange={e => setAccount({...account, bio: e.target.value})}
            placeholder="A few sentences about who you are, your niche, and what makes you unique. Agencies read this before sending proposals."
            className="min-h-[120px]" />
          <p className="text-[11px] text-[#98A2B3] mt-1">{account.bio.length}/500 characters</p>
        </div>
      </SectionCard>

      <SectionCard>
        <h3 className="text-lg font-semibold text-rose-700 mb-3">Danger zone</h3>
        <div className="flex items-center justify-between gap-4 p-4 bg-rose-50 border border-rose-200 rounded-xl">
          <div>
            <p className="text-sm font-medium text-rose-900">Delete account</p>
            <p className="text-xs text-rose-700 mt-0.5">Permanently delete your account, profile, and all data. Active deals must be settled first.</p>
          </div>
          <Button data-testid="delete-account-btn" variant="outline"
            onClick={() => toast.error('Account deletion requires support — contact support@mylikeness.com.au')}
            className="bg-white border-rose-300 text-rose-700 hover:bg-rose-100 flex-shrink-0">
            <Trash className="w-4 h-4 mr-2" />Request deletion
          </Button>
        </div>
      </SectionCard>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   NOTIFICATIONS SECTION
   ═══════════════════════════════════════════════════════════ */
function NotificationsSection() {
  const [prefs, setPrefs] = useState({
    email_new_proposal: true,
    email_proposal_countered: true,
    email_proposal_accepted: true,
    email_content_submitted: true,
    email_payment_released: true,
    email_payout_available: true,
    email_dispute_update: true,
    email_marketing: false,
    email_product_updates: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/creator/notification-preferences')
      .then(r => setPrefs(p => ({ ...p, ...(r.data || {}) })))
      .catch(() => {/* defaults */});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await API.put('/creator/notification-preferences', prefs);
      toast.success('Notification preferences saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const TOGGLES = [
    { group: 'Deals & proposals', items: [
      { k: 'email_new_proposal', label: 'New proposal received', desc: 'When an agency sends you a new offer' },
      { k: 'email_proposal_countered', label: 'Agency responds to your counter', desc: 'Counter-offer accepted, declined, or new terms proposed' },
      { k: 'email_proposal_accepted', label: 'Proposal accepted', desc: 'Confirmation when you or an agency accepts terms' },
    ]},
    { group: 'Content & approvals', items: [
      { k: 'email_content_submitted', label: 'Content submitted for review', desc: 'Agency uploads AI-generated content using your likeness' },
      { k: 'email_dispute_update', label: 'Dispute updates', desc: 'Status changes on any disputed transaction' },
    ]},
    { group: 'Payouts', items: [
      { k: 'email_payment_released', label: 'Payment released to bank', desc: 'Stripe payout has been sent to your account' },
      { k: 'email_payout_available', label: 'Payout ready to claim', desc: 'You have funds waiting to be withdrawn' },
    ]},
    { group: 'Mylikeness updates', items: [
      { k: 'email_product_updates', label: 'Product announcements', desc: 'New features, important changes to the platform' },
      { k: 'email_marketing', label: 'Marketing & tips', desc: 'Occasional emails with creator tips and platform highlights' },
    ]},
  ];

  return (
    <div className="space-y-6" data-testid="notifications-section">
      <SectionCard>
        <div className="flex items-center justify-between mb-6">
          <div><h3 className="text-lg font-semibold text-[#101828]">Email notifications</h3><p className="text-[#475467] text-sm">Choose which emails Mylikeness sends you</p></div>
          <Button onClick={save} disabled={saving} data-testid="save-notifications-btn" className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white">
            <Save className="w-4 h-4 mr-2" />Save
          </Button>
        </div>

        <div className="space-y-6">
          {TOGGLES.map(group => (
            <div key={group.group}>
              <h4 className="text-xs font-semibold text-[#475467] uppercase tracking-wide mb-3">{group.group}</h4>
              <div className="space-y-2">
                {group.items.map(item => (
                  <div key={item.k} className="flex items-center justify-between gap-4 p-3 bg-[#F9FAFB] rounded-xl">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#101828]">{item.label}</p>
                      <p className="text-xs text-[#475467] mt-0.5">{item.desc}</p>
                    </div>
                    <Switch data-testid={`notif-toggle-${item.k}`}
                      checked={!!prefs[item.k]}
                      onCheckedChange={v => setPrefs(p => ({...p, [item.k]: v}))} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECURITY SECTION
   ═══════════════════════════════════════════════════════════ */
function SecuritySection() {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    API.get('/auth/sessions').then(r => setSessions(r.data || [])).catch(() => {});
  }, []);

  const changePassword = async (e) => {
    e?.preventDefault?.();
    if (!oldPw || !newPw) { toast.error('Enter your current and new password'); return; }
    if (newPw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { toast.error('Passwords don\'t match'); return; }
    setSaving(true);
    try {
      await API.post('/auth/change-password', { current_password: oldPw, new_password: newPw });
      toast.success('Password updated');
      setOldPw(''); setNewPw(''); setConfirmPw('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Password change failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="security-section">
      <SectionCard>
        <h3 className="text-lg font-semibold text-[#101828] mb-1">Password</h3>
        <p className="text-[#475467] text-sm mb-5">Change the password used to sign in to Mylikeness.</p>
        <form onSubmit={changePassword} className="space-y-4 max-w-md">
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Current password</Label>
            <Input data-testid="current-password-input" type="password" value={oldPw} onChange={e => setOldPw(e.target.value)} placeholder="••••••••" />
          </div>
          <div>
            <Label className="text-sm font-medium mb-1.5 block">New password</Label>
            <Input data-testid="new-password-input" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="At least 8 characters" />
          </div>
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Confirm new password</Label>
            <Input data-testid="confirm-password-input" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Re-enter new password" />
          </div>
          <Button type="submit" disabled={saving} data-testid="change-password-btn" className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Update password'}
          </Button>
        </form>
      </SectionCard>

      <SectionCard>
        <h3 className="text-lg font-semibold text-[#101828] mb-1">Two-factor authentication</h3>
        <p className="text-[#475467] text-sm mb-4">Add a second layer of security to your account. Coming soon.</p>
        <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-[#101828]">Authenticator app (TOTP)</p>
            <p className="text-xs text-[#475467] mt-0.5">Use Google Authenticator, Authy, 1Password, etc.</p>
          </div>
          <Badge data-testid="2fa-coming-soon" className="bg-blue-50 text-blue-700 border-blue-200">Coming soon</Badge>
        </div>
      </SectionCard>

      <SectionCard>
        <h3 className="text-lg font-semibold text-[#101828] mb-1">Active sessions</h3>
        <p className="text-[#475467] text-sm mb-4">Devices and locations currently signed in to your account.</p>
        {sessions.length === 0 ? (
          <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
            <p className="text-sm text-[#475467]">This device only. We&apos;ll show other active sessions here once available.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sessions.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-3 p-3 bg-[#F9FAFB] rounded-xl">
                <div className="flex items-center gap-3">
                  <Globe className="w-4 h-4 text-[#475467]" />
                  <div>
                    <p className="text-sm text-[#101828] font-medium">{s.device || 'Unknown device'}</p>
                    <p className="text-xs text-[#475467]">{s.location || 'Unknown'} · {s.last_active ? new Date(s.last_active).toLocaleDateString() : ''}</p>
                  </div>
                </div>
                {s.current && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">This device</Badge>}
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   HELP & SUPPORT SECTION
   ═══════════════════════════════════════════════════════════ */
function HelpSupportSection() {
  const [panel, setPanel] = useState('licensing');
  const [openFaq, setOpenFaq] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportPreset, setSupportPreset] = useState({ category: 'general', subject: '', title: 'Contact support' });

  const openSupport = (category, subject, title) => {
    setSupportPreset({ category, subject, title });
    setSupportOpen(true);
  };

  const PANELS = [
    { id: 'licensing', label: 'Licensing types', icon: Shield },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
    { id: 'glossary', label: 'Glossary', icon: FileText },
    { id: 'contact', label: 'Contact & support', icon: MessageSquare },
  ];

  const LICENSE_TYPES = [
    {
      title: 'One-time deal',
      tag: 'one_time',
      color: 'bg-blue-50 text-blue-700 border-blue-200',
      desc: 'A single, fixed-fee agreement. The agency pays once, and the license covers a defined scope (e.g., one campaign, specific content types, limited territory).',
      example: 'A skincare brand pays you $2,000 for the right to use AI-generated images of your likeness in one Instagram campaign running for 3 months in Australia.',
      when: 'Short campaigns, one-off collaborations, or when you prefer a clean, predictable payout with a clear end date.',
    },
    {
      title: 'Subscription license',
      tag: 'subscription',
      color: 'bg-purple-50 text-purple-700 border-purple-200',
      desc: 'A recurring agreement where the agency pays you on a regular billing cycle (monthly, quarterly, etc.). They get ongoing access to generate new content within agreed limits each period.',
      example: 'A fitness brand pays you $800/month. Each month they can generate up to 20 static images and 5 short videos featuring your likeness for their social channels.',
      when: 'Long-term brand partnerships, ambassadorships, or when you want steady recurring income. The subscription includes generation caps, renewal terms, and cancellation rules.',
    },
    {
      title: 'Hybrid deal',
      tag: 'hybrid',
      color: 'bg-amber-50 text-amber-700 border-amber-200',
      desc: 'Combines an upfront one-time payment with an ongoing subscription. Typically the upfront fee covers a sign-on bonus or initial content batch, while the subscription covers ongoing generation.',
      example: 'A fashion label pays a $5,000 sign-on bonus plus $1,200/month for ongoing content generation rights. The sign-on covers an initial video ad campaign; the monthly fee covers social content.',
      when: 'High-value deals where both parties want commitment. The upfront payment de-risks the creator; the subscription gives the agency ongoing flexibility.',
    },
  ];

  const FAQS = [
    { q: 'How do I get paid?', a: 'Once an agency funds a deal and you approve the generated content, funds become "ready to release". Connect your bank via Stripe Express in the Payouts section (takes ~5 minutes), then release funds to your account. Money typically arrives in 2-7 business days.' },
    { q: 'What are Mylikeness commission rates?', a: 'Mylikeness charges the agency (not you) a sliding-scale commission: 10% on deals under $5k, 8% up to $10k, 7% up to $20k, 5% up to $50k, and 3% above $50k. The "net payout" amount you see is what you receive after the platform fee.' },
    { q: 'Do I have to verify my identity?', a: 'Yes, identity verification (government ID + selfie via Stripe) is required before you can receive payouts. Verified creators also earn significantly more and get priority placement. You can browse proposals without verification, but payment requires it.' },
    { q: 'Can I refuse a proposal?', a: 'Absolutely. Refusing has zero impact on your future proposals or standing. Many creators refuse 60-70% of offers. You can also send a counter-proposal to negotiate terms, pricing, or scope.' },
    { q: 'What if an agency creates content I did not approve?', a: 'You always have final approval on generated content. If submitted content violates your permissions or banned categories, use "Request changes" or open a dispute. Funds remain held until resolution. In severe cases, the admin mediation team intervenes.' },
    { q: 'How do subscription deals work?', a: 'In a subscription, the agency pays you on a recurring cycle (e.g., monthly). Each period they can generate content up to agreed caps. You get steady income; they get ongoing access. Either party can cancel with the notice period defined in the agreement. See the "Licensing types" tab for more detail.' },
    { q: 'What happens to content after a deal ends?', a: 'The proposal defines post-termination rules: a takedown window (e.g., 30 days to remove all content), residual use rights (if any), and deletion confirmation requirements. Once the license expires, the agency must comply with these terms or face dispute action.' },
    { q: 'Can I delete my account?', a: 'Yes. Go to Settings → Account → Delete account. Any pending payouts will be processed first. Active deals must be settled or cancelled before deletion completes.' },
    { q: 'What is the morality clause?', a: 'A morality clause lets either party terminate the agreement if certain conditions are met (e.g., criminal conviction, public scandal, reputational harm). The proposal will specify which conditions apply and the consequences (immediate termination, partial payment, etc.).' },
    { q: 'How are taxes handled?', a: 'Mylikeness does not withhold taxes. You receive gross payouts (minus platform commission) and are responsible for reporting income in your jurisdiction. We recommend consulting a tax professional. Stripe provides annual tax summaries for your records.' },
  ];

  const GLOSSARY = [
    { term: 'Likeness', def: 'Your visual identity — face, body, voice, and mannerisms — as used in AI-generated content.' },
    { term: 'Proposal', def: 'A detailed offer from an agency specifying commercial terms, rights, AI generation permissions, and content scope.' },
    { term: 'Counter-proposal', def: 'Your response to a proposal where you accept some terms but request changes to others (pricing, scope, restrictions, etc.).' },
    { term: 'Deal structure', def: 'The payment model: one-time (single fee), subscription (recurring), or hybrid (upfront + recurring).' },
    { term: 'Engagement', def: 'An active, accepted deal where content is being generated and reviewed.' },
    { term: 'Generation cap', def: 'A limit on how many AI-generated assets the agency can create per period (e.g., 20 images/month).' },
    { term: 'Residual use', def: 'Rights for the agency to continue using already-generated content for a limited time after the license ends.' },
    { term: 'Takedown window', def: 'The period after termination in which the agency must remove all content featuring your likeness.' },
    { term: 'Morality clause', def: 'A termination trigger based on conduct or reputation. Can apply to either party depending on the agreement.' },
    { term: 'Cure period', def: 'Time given to fix a breach before the other party can terminate. E.g., "14 days to remedy" means you have 2 weeks to resolve the issue.' },
    { term: 'Governing law', def: 'The legal jurisdiction (e.g., US-California, UK, Australia) whose laws apply to the agreement.' },
    { term: 'Dispute resolution', def: 'How disagreements are handled: negotiation, mediation, arbitration, or litigation.' },
    { term: 'Sensitive alterations', def: 'AI modifications to your likeness that require explicit consent: skin tone, body composition, facial features, age, or gender presentation changes.' },
    { term: 'Stripe Connect', def: 'The payment infrastructure used to send money to your bank account. Set up once in Payouts.' },
    { term: 'Funds held', def: 'Money the agency has paid into escrow for a deal. Held securely until you approve the generated content and release the funds.' },
    { term: 'KYC', def: 'Know Your Customer — identity verification via government ID and selfie, required for receiving payouts.' },
    { term: 'Exclusivity', def: 'A clause restricting you from licensing your likeness to competing brands during the deal period. Can be category-specific or broad.' },
    { term: 'Sign-on bonus', def: 'An upfront payment made at the start of a hybrid deal, separate from the recurring subscription fee.' },
  ];

  return (
    <div className="space-y-6" data-testid="help-support-section">
      <SectionCard>
        <h3 className="text-lg font-semibold text-[#101828] mb-1">Help & support</h3>
        <p className="text-[#475467] text-sm mb-4">Licensing explainers, frequently asked questions, and key terms.</p>

        <div className="flex border-b border-[#E5E7EB] mb-5">
          {PANELS.map(p => (
            <button key={p.id} onClick={() => setPanel(p.id)}
              data-testid={`help-panel-${p.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                panel === p.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-[#475467] hover:text-[#101828]'
              }`}>
              <p.icon className="w-3.5 h-3.5" /> {p.label}
            </button>
          ))}
        </div>

        {/* Licensing Explainer */}
        {panel === 'licensing' && (
          <div className="space-y-4" data-testid="help-licensing-section">
            <p className="text-[#475467] text-sm">Every proposal on Mylikeness uses one of three deal structures. Here is what each one means for you as a creator.</p>
            {LICENSE_TYPES.map(lt => (
              <div key={lt.tag} className="rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid={`license-type-${lt.tag}`}>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className={`text-xs font-semibold ${lt.color}`}>{lt.title}</Badge>
                  </div>
                  <p className="text-[#101828] text-sm leading-relaxed">{lt.desc}</p>
                  <div className="mt-3 p-3 bg-[#F9FAFB] rounded-lg">
                    <p className="text-[10px] text-[#98A2B3] uppercase tracking-wider font-medium mb-1">Example</p>
                    <p className="text-[#475467] text-xs leading-relaxed">{lt.example}</p>
                  </div>
                  <div className="mt-3 p-3 bg-blue-50/50 rounded-lg">
                    <p className="text-[10px] text-blue-500 uppercase tracking-wider font-medium mb-1">Best for</p>
                    <p className="text-[#475467] text-xs leading-relaxed">{lt.when}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* FAQ */}
        {panel === 'faq' && (
          <div className="space-y-2" data-testid="help-faq-section">
            {FAQS.map((item, i) => (
              <div key={i} className="border border-[#E5E7EB] rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`support-faq-${i}`}
                  className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-[#F9FAFB] transition-colors">
                  <span className="text-[#101828] text-sm font-medium">{item.q}</span>
                  {openFaq === i ? <ChevronUp className="w-4 h-4 text-[#475467] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#475467] flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 pt-1 text-[#475467] text-xs leading-relaxed bg-[#F9FAFB] border-t border-[#E5E7EB]">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Glossary */}
        {panel === 'glossary' && (
          <div data-testid="help-glossary-section">
            <div className="grid grid-cols-1 gap-2">
              {GLOSSARY.map((g, i) => (
                <div key={i} className="flex items-start gap-3 px-4 py-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]" data-testid={`glossary-term-${i}`}>
                  <span className="text-[#101828] text-sm font-semibold min-w-[140px] flex-shrink-0">{g.term}</span>
                  <span className="text-[#475467] text-xs leading-relaxed">{g.def}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact & Support */}
        {panel === 'contact' && (
          <div className="space-y-4" data-testid="help-contact-section">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { title: 'General enquiries', desc: 'Questions about your account, proposals, or the platform.', category: 'general', subject: 'General enquiry', icon: MessageSquare },
                { title: 'Report an issue', desc: 'Bug reports, suspicious activity, or platform misuse.', category: 'report_issue', subject: 'Report an issue', icon: AlertCircle },
                { title: 'Payout & billing help', desc: 'Payment delays, tax questions, or Stripe setup issues.', category: 'payout_billing', subject: 'Payout help', icon: DollarSign },
                { title: 'Trust & safety', desc: 'Content misuse, identity concerns, or safety reports.', category: 'trust_safety', subject: 'Trust and safety', icon: Lock },
              ].map((c, i) => (
                <button key={i} type="button"
                  onClick={() => openSupport(c.category, c.subject, c.title)}
                  data-testid={`contact-card-${i}`}
                  className="w-full text-left flex items-start gap-3 p-4 rounded-xl border border-[#E5E7EB] hover:border-blue-300 hover:bg-blue-50/30 transition-all group">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <c.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-[#101828] text-sm group-hover:text-blue-700 transition-colors">{c.title}</h4>
                    <p className="text-[#475467] text-xs mt-0.5">{c.desc}</p>
                    <p className="text-blue-600 text-xs mt-1 font-medium">Send us a message →</p>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-[#98A2B3] text-xs text-center pt-2">Typical response time: within 24 hours on business days.</p>
          </div>
        )}
      </SectionCard>
      <SupportDialog
        open={supportOpen}
        onOpenChange={setSupportOpen}
        defaultCategory={supportPreset.category}
        defaultSubject={supportPreset.subject}
        title={supportPreset.title}
      />
    </div>
  );
}
